/**
 * @fileoverview Componente responsável por editar a configuração do nó ou do fluxo selecionado.
 */

import React from "react";
import { IconButton, Tooltip, CircularProgress } from "@material-ui/core";
import {
  Check, X, Info, Globe, Plus, Trash2, FileUp, Upload, Maximize2,
  Bot, Minimize2, Brain, Pencil, Sparkles, ChevronDown, ChevronUp,
  SquareKanban, RefreshCw, ArrowRightLeft, GripVertical, Send,
  ShoppingBag, AlertTriangle, BookOpen,
} from "lucide-react";

import { NODE_TYPES_CONFIG } from "../constants";
import BulkFilterPanel from "./BulkFilterPanel";
import BulkListPanel from "./BulkListPanel";
import TemplateVariableMapper from "./TemplateVariableMapper";
import AiToolsGuideModal from "../components/AiToolsGuideModal";
import { formatRelativeTime } from "../../../../utils/formatRelativeTime";

/**
 * Config panel (right side) for editing selected node properties.
 */
var ConfigPanel = function (props) {
  var classes = props.classes;
  var aiGuideState = React.useState(false);
  var guideOpen = aiGuideState[0];
  var setGuideOpen = aiGuideState[1];

  var aiUsersState = React.useState([]);
  var aiUsers = aiUsersState[0];
  var setAiUsers = aiUsersState[1];

  var aiUsersLoadingState = React.useState(false);
  var aiUsersLoading = aiUsersLoadingState[0];
  var setAiUsersLoading = aiUsersLoadingState[1];

  React.useEffect(function () {
    if (!props.selectedNode || props.selectedNode.data.nodeType !== "ai_agent") return;
    var cancelled = false;
    setAiUsersLoading(true);
    props.api
      .get("/users/list?onlyAi=true")
      .then(function (resp) {
        if (cancelled) return;
        setAiUsers(Array.isArray(resp.data) ? resp.data : []);
      })
      .catch(function () {
        if (!cancelled) setAiUsers([]);
      })
      .finally(function () {
        if (!cancelled) setAiUsersLoading(false);
      });
    return function () { cancelled = true; };
  }, [props.selectedNode && props.selectedNode.id]);
  var selectedNode = props.selectedNode;
  var setSelectedNodeId = props.setSelectedNodeId;
  var updateNodeData = props.updateNodeData;
  var queues = props.queues;
  var whatsapps = props.whatsapps;
  var setPromptModalOpen = props.setPromptModalOpen;
  var fileUploading = props.fileUploading;
  var setFileUploading = props.setFileUploading;
  var pendingFileName = props.pendingFileName;
  var setPendingFileName = props.setPendingFileName;
  var pendingFile = props.pendingFile;
  var setPendingFile = props.setPendingFile;
  var toastError = props.toastError;
  var api = props.api;
  var memName = props.memName;
  var setMemName = props.setMemName;
  var memContent = props.memContent;
  var setMemContent = props.setMemContent;
  var memSummary = props.memSummary;
  var setMemSummary = props.setMemSummary;
  var memGenerating = props.memGenerating;
  var setMemGenerating = props.setMemGenerating;
  var memEditingId = props.memEditingId;
  var setMemEditingId = props.setMemEditingId;
  var memExpandedId = props.memExpandedId;
  var setMemExpandedId = props.setMemExpandedId;
  var leadStatuses = props.leadStatuses || [];
  var setLeadStatuses = props.setLeadStatuses;
  var crmBoards = props.crmBoards || [];
  var crmFieldName = props.crmFieldName;
  var setCrmFieldName = props.setCrmFieldName;
  var setMemPromptModalOpen = props.setMemPromptModalOpen;
  var users = props.users || [];
  var edges = props.edges || [];
  var nodes = props.nodes || [];
  var wooIntegrations = props.wooIntegrations || [];

  if (!selectedNode) return null;

  var cfg = NODE_TYPES_CONFIG.find(function (n) { return n.type === selectedNode.data.nodeType; }) || NODE_TYPES_CONFIG[0];
  var Icon = cfg.icon;
  var nodeData = selectedNode.data;
  var selectedQueues = nodeData.queues || [];
  var selectedWhatsapps = nodeData.whatsapps || [];

  var toggleQueue = function (queueId) {
    var current = nodeData.queues || [];
    var idx = current.indexOf(queueId);
    if (idx === -1) {
      updateNodeData("queues", current.concat(queueId));
    } else {
      updateNodeData("queues", current.filter(function (id) { return id !== queueId; }));
    }
  };

  var toggleWhatsapp = function (waId) {
    var current = nodeData.whatsapps || [];
    var idx = current.indexOf(waId);
    if (idx === -1) {
      updateNodeData("whatsapps", current.concat(waId));
    } else {
      updateNodeData("whatsapps", current.filter(function (id) { return id !== waId; }));
    }
  };

  return (
    <div className={classes.configPanel}>
      <div className={classes.configHeader}>
        <div
          className={classes.configHeaderIcon}
          style={{ background: cfg.color + "18" }}
        >
          <Icon size={16} color={cfg.color} strokeWidth={2} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={classes.configHeaderTitle}>{cfg.label}</div>
          <div className={classes.configHeaderDesc}>{cfg.desc}</div>
        </div>
        <IconButton
          size="small"
          className={classes.configClose}
          onClick={function () { setSelectedNodeId(null); }}
        >
          <X size={16} />
        </IconButton>
      </div>

      <div className={classes.configBody}>
        {cfg.type === "receive_message" && (
          <React.Fragment>
            {/* Receber do setor */}
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Receber do setor</span>
                <Tooltip
                  title="O fluxo sera acionado apenas quando um cliente que esta em um dos setores selecionados enviar uma mensagem. Se nenhum setor for selecionado, o fluxo nao sera acionado por nenhum setor."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>

              {selectedQueues.length > 0 && (
                <div className={classes.configChips}>
                  {selectedQueues.map(function (qId) {
                    var q = queues.find(function (queue) { return queue.id === qId; });
                    if (!q) return null;
                    return (
                      <span key={qId} className={classes.configChip}>
                        <span
                          className={classes.configDropdownColor}
                          style={{ background: q.color || "#5B7CFF" }}
                        />
                        {q.name}
                        <span
                          className={classes.configChipRemove}
                          onClick={function () { toggleQueue(qId); }}
                        >
                          <X size={10} />
                        </span>
                      </span>
                    );
                  })}
                </div>
              )}

              <div className={classes.configDropdown} style={{ marginTop: 6 }}>
                {queues.length === 0 && (
                  <div className={classes.configEmpty}>Nenhum setor encontrado</div>
                )}
                {queues.map(function (q) {
                  var isSelected = selectedQueues.indexOf(q.id) !== -1;
                  return (
                    <div
                      key={q.id}
                      className={classes.configDropdownItem}
                      onClick={function () { toggleQueue(q.id); }}
                    >
                      <div
                        className={
                          classes.configDropdownCheck +
                          (isSelected ? " " + classes.configDropdownCheckActive : "")
                        }
                      >
                        {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                      </div>
                      <span
                        className={classes.configDropdownColor}
                        style={{ background: q.color || "#5B7CFF" }}
                      />
                      {q.name}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Receber da conexao */}
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Receber da conexao</span>
                <Tooltip
                  title="O fluxo sera acionado apenas para mensagens recebidas nas conexoes selecionadas. Se nenhuma conexao for selecionada, o fluxo nao sera acionado por nenhuma conexao."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>

              {selectedWhatsapps.length > 0 && (
                <div className={classes.configChips}>
                  {selectedWhatsapps.map(function (waId) {
                    var wa = whatsapps.find(function (w) { return w.id === waId; });
                    if (!wa) return null;
                    return (
                      <span key={waId} className={classes.configChip}>
                        {wa.name}
                        <span
                          className={classes.configChipRemove}
                          onClick={function () { toggleWhatsapp(waId); }}
                        >
                          <X size={10} />
                        </span>
                      </span>
                    );
                  })}
                </div>
              )}

              <div className={classes.configDropdown} style={{ marginTop: 6 }}>
                {whatsapps.length === 0 && (
                  <div className={classes.configEmpty}>Nenhuma conexao encontrada</div>
                )}
                {whatsapps.map(function (wa) {
                  var isSelected = selectedWhatsapps.indexOf(wa.id) !== -1;
                  return (
                    <div
                      key={wa.id}
                      className={classes.configDropdownItem}
                      onClick={function () { toggleWhatsapp(wa.id); }}
                    >
                      <div
                        className={
                          classes.configDropdownCheck +
                          (isSelected ? " " + classes.configDropdownCheckActive : "")
                        }
                      >
                        {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                      </div>
                      {wa.name}
                    </div>
                  );
                })}
              </div>
            </div>
          </React.Fragment>
        )}

        {cfg.type === "bulk_message" && (
          <React.Fragment>
            {/* Aviso de banimento */}
            <div className={classes.configField}>
              <div style={{
                padding: "10px 12px",
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.25)",
                borderRadius: 8,
                fontSize: 11,
                lineHeight: 1.5,
                color: "rgba(255,255,255,0.8)",
              }}>
                <strong style={{ color: "#ef4444" }}>⚠ Aviso importante</strong>
                <br />
                O uso de disparo em massa pode resultar no <strong style={{ color: "#ef4444" }}>banimento permanente</strong> do número pela Meta/WhatsApp. Não nos responsabilizamos pelo uso desta funcionalidade. Use com moderação e siga as políticas do WhatsApp Business.
              </div>
            </div>

            {/* Conexão API Oficial */}
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Conexão API Oficial</span>
                <Tooltip
                  title="Selecione a conexão da API oficial da Meta para enviar as mensagens em massa. Apenas conexões da API oficial são permitidas."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>

              <div className={classes.configDropdown} style={{ marginTop: 6 }}>
                {(function () {
                  var officialConns = whatsapps.filter(function (wa) { return wa.provider === "meta-api"; });
                  if (officialConns.length === 0) {
                    return (
                      <div className={classes.configEmpty}>
                        Nenhuma conexão API oficial encontrada
                      </div>
                    );
                  }
                  return officialConns.map(function (wa) {
                    var isSelected = nodeData.whatsappId === wa.id;
                    return (
                      <div
                        key={wa.id}
                        className={classes.configDropdownItem}
                        onClick={function () {
                          updateNodeData("whatsappId", isSelected ? null : wa.id);
                        }}
                      >
                        <div
                          className={
                            classes.configDropdownCheck +
                            (isSelected ? " " + classes.configDropdownCheckActive : "")
                          }
                        >
                          {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                        </div>
                        {wa.name}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Seletor de fonte de contatos (CSV vs Filtro) */}
            {(function () {
              var source = nodeData.source || "csv";
              var options = [
                { value: "csv", label: "CSV" },
                { value: "filter", label: "Filtro" },
                { value: "list", label: "Lista salva" },
              ];
              return (
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Fonte de contatos</span>
                    <Tooltip
                      title="CSV: importar planilha. Filtro: selecionar contatos cadastrados na plataforma por critérios (nome, email, data, etc.)."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    {options.map(function (opt) {
                      var active = source === opt.value;
                      return (
                        <div
                          key={opt.value}
                          onClick={function () {
                            updateNodeData("source", opt.value);
                          }}
                          style={{
                            flex: 1,
                            padding: "7px 10px",
                            borderRadius: 8,
                            cursor: "pointer",
                            textAlign: "center",
                            fontSize: 11,
                            fontWeight: 600,
                            background: active ? "rgba(99,102,241,0.18)" : "rgba(255,255,255,0.03)",
                            border: active
                              ? "1px solid rgba(99,102,241,0.5)"
                              : "1px solid rgba(255,255,255,0.08)",
                            color: active ? "#a5b4fc" : "rgba(255,255,255,0.6)",
                            transition: "all 0.15s",
                          }}
                        >
                          {opt.label}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Modo FILTRO — seleção dinâmica de contatos da plataforma */}
            {(nodeData.source === "filter") && (function () {
              // Descobre templateName conectado para oferecer dedup
              var edge = (props.edges || []).find(function (e) { return e.source === selectedNode.id; });
              var tplNode = edge
                ? (props.nodes || []).find(function (n) { return n.id === edge.target && n.data && n.data.nodeType === "send_template"; })
                : null;
              var tplName = tplNode && tplNode.data ? tplNode.data.templateName : null;

              var handleSaveAsList = async function () {
                var filter = nodeData.filter || {};
                var hasAny =
                  filter.nameContains || filter.numberContains || filter.emailContains ||
                  filter.createdFrom || filter.createdTo ||
                  filter.lastInteractionFrom || filter.lastInteractionTo ||
                  filter.excludeReceivedTemplateSince;
                if (!hasAny) {
                  if (toastError) toastError({ response: { data: { error: "Defina pelo menos um filtro antes de salvar" } } });
                  return;
                }
                var name = (window.prompt("Nome da lista:") || "").trim();
                if (!name) return;
                try {
                  var resp = await api.post("/contact-segments", { name: name, criteria: filter });
                  if (resp && resp.data && resp.data.id) {
                    // Após salvar, alternar para o modo Lista já com a recém-criada selecionada
                    updateNodeData("source", "list");
                    updateNodeData("segmentId", resp.data.id);
                  }
                } catch (err) {
                  if (toastError) toastError(err);
                }
              };

              return (
                <div className={classes.configField}>
                  <BulkFilterPanel
                    filter={nodeData.filter}
                    onFilterChange={function (next) { updateNodeData("filter", next); }}
                    templateName={tplName}
                    classes={classes}
                  />
                  <div
                    onClick={handleSaveAsList}
                    style={{
                      marginTop: 10,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 12px",
                      borderRadius: 6,
                      background: "rgba(255,255,255,0.04)",
                      border: "1px dashed rgba(255,255,255,0.12)",
                      color: "rgba(255,255,255,0.7)",
                      cursor: "pointer",
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    Salvar este filtro como lista
                  </div>
                </div>
              );
            })()}

            {/* Modo LISTA SALVA — escolher segmento existente */}
            {(nodeData.source === "list") && (
              <div className={classes.configField}>
                <BulkListPanel
                  segmentId={nodeData.segmentId}
                  onSegmentChange={function (id) { updateNodeData("segmentId", id); }}
                  onCreateNew={function () {
                    // Ao criar nova, voltar pro modo Filtro (operador preenche lá e depois Salva)
                    updateNodeData("source", "filter");
                    updateNodeData("segmentId", null);
                  }}
                  toastError={toastError}
                />
              </div>
            )}

            {/* Modo CSV — importar planilha (comportamento legado) */}
            {(!nodeData.source || nodeData.source === "csv") && (
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Contatos</span>
                <Tooltip
                  title="Importe uma planilha CSV com os contatos para o disparo. O arquivo deve conter as colunas: nome, número."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>

              {/* Botões importar + exportar modelo */}
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <label style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  padding: "8px 0", borderRadius: 8, cursor: "pointer",
                  background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.2)",
                  fontSize: 11, fontWeight: 600, color: "#818cf8", transition: "all 0.15s",
                }}>
                  <Upload size={13} />
                  Importar CSV
                  <input
                    type="file"
                    accept=".csv,.txt"
                    style={{ display: "none" }}
                    onChange={function (e) {
                      var file = e.target.files && e.target.files[0];
                      if (!file) return;
                      var reader = new FileReader();
                      reader.onload = function (ev) {
                        var text = ev.target.result;
                        var lines = text.split(/\r?\n/).filter(function (l) { return l.trim(); });
                        if (lines.length === 0) return;
                        // Detectar se primeira linha e header
                        var startIdx = 0;
                        var firstLine = lines[0].toLowerCase().replace(/\s/g, "");
                        if (firstLine.indexOf("nome") !== -1 && firstLine.indexOf("numero") !== -1) {
                          startIdx = 1;
                        }
                        var contacts = [];
                        for (var i = startIdx; i < lines.length; i++) {
                          var parts = lines[i].split(/[,;]/);
                          if (parts.length < 2) continue;
                          var name = parts[0].trim();
                          var number = parts[1].trim().replace(/\D/g, "");
                          if (name && number) {
                            contacts.push({ name: name, number: number });
                          }
                        }
                        updateNodeData("contacts", contacts);
                      };
                      reader.readAsText(file, "UTF-8");
                      e.target.value = "";
                    }}
                  />
                </label>
                <div
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    padding: "8px 12px", borderRadius: 8, cursor: "pointer",
                    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
                    fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.5)", transition: "all 0.15s",
                  }}
                  onClick={function () {
                    var csv = "nome,numero\nteste,5511999999999\n";
                    var blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                    var url = URL.createObjectURL(blob);
                    var a = document.createElement("a");
                    a.href = url;
                    a.download = "modelo_contatos.csv";
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  Exportar modelo
                </div>
              </div>

              {/* Formato esperado */}
              <div style={{
                marginTop: 8, padding: "8px 10px", borderRadius: 6,
                background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)",
                fontFamily: "monospace", fontSize: 10, lineHeight: 1.6,
                color: "rgba(255,255,255,0.35)",
              }}>
                <div style={{ color: "rgba(255,255,255,0.5)", fontWeight: 600, marginBottom: 2 }}>Formato do CSV:</div>
                <div>nome,numero</div>
                <div>teste,5511999999999</div>
              </div>

              {/* Preview contatos importados */}
              {(function () {
                var contacts = nodeData.contacts || [];
                if (contacts.length === 0) return null;
                return (
                  <div style={{ marginTop: 8 }}>
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      marginBottom: 6,
                    }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>
                        {contacts.length} contato{contacts.length !== 1 ? "s" : ""} importado{contacts.length !== 1 ? "s" : ""}
                      </span>
                      <span
                        style={{
                          fontSize: 10, color: "#ef4444", cursor: "pointer",
                          fontWeight: 600, opacity: 0.8,
                        }}
                        onClick={function () { updateNodeData("contacts", []); }}
                      >
                        Limpar
                      </span>
                    </div>
                    <div style={{
                      maxHeight: 160, overflowY: "auto", borderRadius: 6,
                      border: "1px solid rgba(255,255,255,0.06)",
                    }}>
                      <table style={{ width: "100%", fontSize: 10, borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                            <th style={{ padding: "5px 8px", textAlign: "left", fontWeight: 600, color: "rgba(255,255,255,0.5)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>Nome</th>
                            <th style={{ padding: "5px 8px", textAlign: "left", fontWeight: 600, color: "rgba(255,255,255,0.5)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>Número</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contacts.slice(0, 50).map(function (c, i) {
                            return (
                              <tr key={i} style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                                <td style={{ padding: "4px 8px", color: "rgba(255,255,255,0.6)" }}>{c.name}</td>
                                <td style={{ padding: "4px 8px", color: "rgba(255,255,255,0.4)", fontFamily: "monospace" }}>{c.number}</td>
                              </tr>
                            );
                          })}
                          {contacts.length > 50 && (
                            <tr>
                              <td colSpan={2} style={{ padding: "4px 8px", color: "rgba(255,255,255,0.3)", textAlign: "center", fontSize: 10 }}>
                                ... e mais {contacts.length - 50} contatos
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </div>
            )}

            {/* Intervalo entre envios */}
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Intervalo entre envios</span>
                <Tooltip
                  title="Tempo em milissegundos entre cada envio. Valores maiores reduzem o risco de bloqueio. Recomendado: 1000ms (1 segundo) ou mais."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  className={classes.configInput}
                  type="number"
                  min="50"
                  max="60000"
                  step="50"
                  value={nodeData.sendInterval || 1000}
                  onChange={function (e) {
                    var val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 50) updateNodeData("sendInterval", val);
                  }}
                  style={{ width: 90, textAlign: "center" }}
                />
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>ms</span>
                <span style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginLeft: 4 }}>
                  ({((nodeData.sendInterval || 1000) / 1000).toFixed(1)}s por envio)
                </span>
              </div>
              {(nodeData.sendInterval || 1000) < 500 && (
                <div style={{ fontSize: 10, color: "#f59e0b", marginTop: 4 }}>
                  Intervalos muito curtos aumentam o risco de banimento
                </div>
              )}
            </div>

            {/* Botão disparar */}
            {(function () {
              var source = nodeData.source || "csv";
              var contacts = nodeData.contacts || [];
              var filter = nodeData.filter || {};
              var hasConnection = !!nodeData.whatsappId;
              var bulkSending = props.bulkSending || false;
              var bulkProgress = props.bulkProgress;
              var handleBulkSend = props.handleBulkSend;
              // Para filter, não temos o total exato aqui no render (vem do hook dentro do BulkFilterPanel).
              // Validação final e count real ficam no backend; aqui só exigimos conexão + algum filtro preenchido.
              var hasAnyFilter =
                !!filter.nameContains ||
                !!filter.numberContains ||
                !!filter.emailContains ||
                !!filter.createdFrom ||
                !!filter.createdTo ||
                !!filter.lastInteractionFrom ||
                !!filter.lastInteractionTo ||
                !!filter.excludeReceivedTemplateSince;
              var sourceReady;
              if (source === "csv") sourceReady = contacts.length > 0;
              else if (source === "filter") sourceReady = hasAnyFilter;
              else if (source === "list") sourceReady = !!nodeData.segmentId;
              else sourceReady = false;

              // Descobre o node send_template conectado e valida se todas as
              // variáveis do template têm mapeamento. Bloqueia disparo se faltar.
              var tplEdge = (props.edges || []).find(function (e) { return e.source === selectedNode.id; });
              var tplNode = tplEdge
                ? (props.nodes || []).find(function (n) { return n.id === tplEdge.target && n.data && n.data.nodeType === "send_template"; })
                : null;
              var tplBody = tplNode && tplNode.data && tplNode.data.selectedTemplate
                ? tplNode.data.selectedTemplate.bodyText || ""
                : "";
              var tplVars = [];
              if (tplBody) {
                var seen = {};
                var re = /\{\{(\d+)\}\}/g;
                var m;
                while ((m = re.exec(tplBody)) !== null) {
                  if (!seen[m[1]]) { seen[m[1]] = true; tplVars.push(m[1]); }
                }
              }
              var mapping = (tplNode && tplNode.data && tplNode.data.variableMapping) || {};
              var missingVars = tplVars.filter(function (k) { return !mapping[k]; });
              var variablesOk = missingVars.length === 0;

              var canSend = hasConnection && sourceReady && variablesOk && !bulkSending;
              var sendLabel;
              if (!variablesOk) sendLabel = "Mapeie as variáveis (" + missingVars.length + ")";
              else if (source === "csv") sendLabel = "Disparar (" + contacts.length + " contatos)";
              else if (source === "list") sendLabel = "Disparar lista";
              else sendLabel = "Disparar";

              return (
                <div className={classes.configField} style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                      padding: "12px 0", borderRadius: 10, cursor: canSend ? "pointer" : "not-allowed",
                      background: canSend ? "linear-gradient(135deg, #6366f1, #8b5cf6)" : "rgba(255,255,255,0.06)",
                      color: canSend ? "#fff" : "rgba(255,255,255,0.25)",
                      fontWeight: 700, fontSize: 13, transition: "all 0.2s",
                      opacity: bulkSending ? 0.7 : 1,
                    }}
                    onClick={function () { if (canSend && handleBulkSend) handleBulkSend(); }}
                  >
                    {bulkSending ? (
                      <CircularProgress size={14} style={{ color: "#fff" }} />
                    ) : (
                      <Send size={15} />
                    )}
                    {bulkSending ? "Enviando..." : sendLabel}
                  </div>

                  {/* Progress bar */}
                  {bulkProgress && !bulkProgress.done && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{
                        display: "flex", justifyContent: "space-between", fontSize: 10,
                        color: "rgba(255,255,255,0.5)", marginBottom: 4,
                      }}>
                        <span>{bulkProgress.current || 0} / {bulkProgress.total || 0}</span>
                        <span>{bulkProgress.percent || 0}%</span>
                      </div>
                      <div style={{
                        height: 6, borderRadius: 3, background: "rgba(255,255,255,0.06)", overflow: "hidden",
                      }}>
                        <div style={{
                          height: "100%", borderRadius: 3, transition: "width 0.3s ease",
                          background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
                          width: (bulkProgress.percent || 0) + "%",
                        }} />
                      </div>
                      <div style={{
                        display: "flex", justifyContent: "space-between", fontSize: 10,
                        color: "rgba(255,255,255,0.4)", marginTop: 4,
                      }}>
                        <span style={{ color: "#22c55e" }}>{bulkProgress.sent || 0} enviados</span>
                        {(bulkProgress.failed || 0) > 0 && (
                          <span style={{ color: "#ef4444" }}>{bulkProgress.failed} falhas</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Resultado final */}
                  {bulkProgress && bulkProgress.done && (
                    <div style={{
                      marginTop: 8, padding: "8px 12px", borderRadius: 8,
                      background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.15)",
                      fontSize: 11, color: "rgba(255,255,255,0.6)",
                    }}>
                      Concluído em {bulkProgress.elapsed}s — {bulkProgress.sent} enviados
                      {(bulkProgress.failed || 0) > 0 ? ", " + bulkProgress.failed + " falhas" : ""}
                    </div>
                  )}

                  {/* Validação */}
                  {!hasConnection && (
                    <div style={{ fontSize: 10, color: "#f59e0b", marginTop: 6 }}>
                      Selecione uma conexão API oficial
                    </div>
                  )}
                </div>
              );
            })()}
          </React.Fragment>
        )}

        {cfg.type === "send_template" && (
          <React.Fragment>
            {/* Template selector */}
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Template</span>
                <Tooltip
                  title="Selecione o template aprovado pela Meta para enviar no disparo. Conecte este node ao node de Disparo em Massa para carregar os templates da conexao."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>

              {(function () {
                var templates = props.bulkTemplates || [];
                var loadingTemplates = props.loadingTemplates || false;
                var selectedTemplateId = nodeData.templateName || null;

                if (loadingTemplates) {
                  return (
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      padding: "16px 0", color: "rgba(255,255,255,0.4)", fontSize: 11,
                    }}>
                      <CircularProgress size={14} style={{ color: "#6366f1", marginRight: 8 }} />
                      Carregando templates...
                    </div>
                  );
                }

                if (templates.length === 0) {
                  return (
                    <div style={{
                      padding: "10px 12px", borderRadius: 8,
                      background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.12)",
                      fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 6,
                    }}>
                      Nenhum template encontrado. Conecte este node a um node "Disparo em massa" com uma conexao API oficial configurada.
                    </div>
                  );
                }

                return (
                  <div className={classes.configDropdown} style={{ marginTop: 6, maxHeight: 200, overflowY: "auto" }}>
                    {templates.map(function (tpl) {
                      var isSelected = selectedTemplateId === tpl.name;
                      return (
                        <div
                          key={tpl.name + "_" + tpl.language}
                          className={classes.configDropdownItem}
                          style={{ flexDirection: "column", alignItems: "flex-start", padding: "8px 10px" }}
                          onClick={function () {
                            if (isSelected) {
                              updateNodeData("templateName", null);
                              updateNodeData("selectedTemplate", null);
                            } else {
                              updateNodeData("templateName", tpl.name);
                              var bodyComp = (tpl.components || []).find(function (c) { return c.type === "BODY"; });
                              var btnComp = (tpl.components || []).find(function (c) { return c.type === "BUTTONS"; });
                              var headerComp = (tpl.components || []).find(function (c) { return c.type === "HEADER"; });
                              var buttons = btnComp ? (btnComp.buttons || []).map(function (b) {
                                return { text: b.text, type: b.type };
                              }) : [];
                              updateNodeData("selectedTemplate", {
                                name: tpl.name,
                                language: tpl.language,
                                bodyText: bodyComp ? bodyComp.text : "",
                                buttons: buttons,
                                header: headerComp ? {
                                  format: headerComp.format,
                                  text: headerComp.text || null,
                                } : null,
                              });
                            }
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 6, width: "100%" }}>
                            <div
                              className={
                                classes.configDropdownCheck +
                                (isSelected ? " " + classes.configDropdownCheckActive : "")
                              }
                            >
                              {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 11 }}>{tpl.name}</div>
                              <div style={{ fontSize: 9, opacity: 0.4 }}>{tpl.language} • {tpl.status}</div>
                            </div>
                          </div>
                          {isSelected && (function () {
                            var headerComp = (tpl.components || []).find(function (c) { return c.type === "HEADER"; });
                            if (!headerComp) return null;
                            var fmt = String(headerComp.format || "").toUpperCase();
                            var label = fmt === "TEXT" ? "Cabeçalho: Texto"
                              : fmt === "IMAGE" ? "Cabeçalho: Imagem"
                              : fmt === "VIDEO" ? "Cabeçalho: Vídeo"
                              : fmt === "DOCUMENT" ? "Cabeçalho: Documento"
                              : null;
                            if (!label) return null;
                            return (
                              <div style={{
                                marginTop: 6, padding: "4px 8px", borderRadius: 6,
                                background: "rgba(99,102,241,0.08)",
                                border: "1px solid rgba(99,102,241,0.18)",
                                fontSize: 10, lineHeight: 1.4,
                                color: "rgba(255,255,255,0.65)",
                                width: "100%",
                              }}>
                                {label}
                                {fmt === "TEXT" && headerComp.text ? (
                                  <span style={{ opacity: 0.55 }}> — {headerComp.text}</span>
                                ) : null}
                              </div>
                            );
                          })()}
                          {isSelected && (function () {
                            var bodyComp = (tpl.components || []).find(function (c) { return c.type === "BODY"; });
                            if (!bodyComp) return null;
                            return (
                              <div style={{
                                marginTop: 6, padding: "6px 8px", borderRadius: 6,
                                background: "rgba(255,255,255,0.03)", fontSize: 10,
                                lineHeight: 1.5, color: "rgba(255,255,255,0.5)",
                                whiteSpace: "pre-wrap", width: "100%",
                              }}>
                                {bodyComp.text}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Mapeamento de variáveis do template selecionado */}
            {nodeData.selectedTemplate && (
              <div className={classes.configField}>
                <TemplateVariableMapper
                  selectedTemplate={nodeData.selectedTemplate}
                  mapping={nodeData.variableMapping}
                  onChange={function (next) { updateNodeData("variableMapping", next); }}
                />
              </div>
            )}

            {/* Metricas */}
            {(function () {
              var stats = nodeData.stats || {};
              var sent = stats.sent || 0;
              if (sent === 0) return null;
              var delivered = stats.delivered || 0;
              var read = stats.read || 0;
              return (
                <div className={classes.configField}>
                  <span className={classes.configLabel}>Metricas</span>
                  <div style={{
                    display: "flex", gap: 8, marginTop: 6,
                  }}>
                    <div style={{
                      flex: 1, textAlign: "center", padding: "8px 0", borderRadius: 8,
                      background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.12)",
                    }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#22c55e" }}>{sent}</div>
                      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)" }}>Enviados</div>
                    </div>
                    <div style={{
                      flex: 1, textAlign: "center", padding: "8px 0", borderRadius: 8,
                      background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.12)",
                    }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#22c55e" }}>{sent > 0 ? ((delivered / sent) * 100).toFixed(1) : "0.0"}%</div>
                      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)" }}>Entregue</div>
                    </div>
                    <div style={{
                      flex: 1, textAlign: "center", padding: "8px 0", borderRadius: 8,
                      background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.12)",
                    }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#22c55e" }}>{sent > 0 ? ((read / sent) * 100).toFixed(1) : "0.0"}%</div>
                      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)" }}>Lido</div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Info */}
            <div style={{
              padding: "10px 12px", borderRadius: 8,
              background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.12)",
              fontSize: 11, lineHeight: 1.5, color: "rgba(255,255,255,0.5)", marginTop: 4,
            }}>
              Conecte ao node "Disparo em massa" para definir a conexao e os contatos. O template selecionado sera enviado para todos os contatos importados.
            </div>
          </React.Fragment>
        )}

        {cfg.type === "ai_agent" && (
          <React.Fragment>
            {/* Botão de guia das automações */}
            <div className={classes.configField}>
              <button
                onClick={function () { setGuideOpen(true); }}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  background: "rgba(91,124,255,0.10)",
                  border: "1px solid rgba(91,124,255,0.25)",
                  color: "rgba(255,255,255,0.88)",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  transition: "background 0.15s, border-color 0.15s",
                }}
                onMouseEnter={function (e) {
                  e.currentTarget.style.background = "rgba(91,124,255,0.16)";
                  e.currentTarget.style.borderColor = "rgba(91,124,255,0.40)";
                }}
                onMouseLeave={function (e) {
                  e.currentTarget.style.background = "rgba(91,124,255,0.10)";
                  e.currentTarget.style.borderColor = "rgba(91,124,255,0.25)";
                }}
              >
                <BookOpen size={14} />
                Guia das automações da IA
              </button>
            </div>

            {/* Usuário IA representante */}
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Usuário IA representante</span>
                <Tooltip
                  title="Este usuário aparecerá como atendente nos tickets enquanto a IA estiver respondendo. Permite que a equipe veja em /tickets quem está atendendo cada conversa. Apenas usuários marcados como Usuário IA aparecem aqui."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>
              {aiUsersLoading && (
                <div style={{
                  padding: "12px 14px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(255,255,255,0.03)",
                  fontSize: 12,
                  color: "rgba(255,255,255,0.55)",
                  display: "flex",
                  alignItems: "center",
                  gap: 8
                }}>
                  <CircularProgress size={12} style={{ color: "rgba(255,255,255,0.55)" }} />
                  Carregando usuários IA…
                </div>
              )}
              {!aiUsersLoading && aiUsers.length === 0 && (
                <div style={{
                  padding: "12px 14px",
                  borderRadius: 8,
                  border: "1px dashed rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.02)",
                  fontSize: 11,
                  color: "rgba(255,255,255,0.5)",
                  textAlign: "center",
                  lineHeight: 1.5
                }}>
                  Nenhum Usuário IA cadastrado.<br />
                  Crie um usuário marcado como "Usuário IA" em <strong>Usuários</strong> para listar aqui.
                </div>
              )}
              {!aiUsersLoading && aiUsers.length > 0 && (
                <div style={{
                  maxHeight: 180, overflowY: "auto", marginTop: 4,
                  borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)"
                }}>
                  {/* Opção "Nenhum" */}
                  {(function () {
                    var isSelected = !nodeData.aiUserId;
                    return (
                      <div
                        style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "8px 12px", cursor: "pointer",
                          background: isSelected ? "rgba(91,124,255,0.12)" : "transparent",
                          borderBottom: "1px solid rgba(255,255,255,0.04)",
                          transition: "background 0.15s"
                        }}
                        onClick={function () { updateNodeData("aiUserId", null); }}
                      >
                        <div style={{
                          width: 18, height: 18, borderRadius: "50%",
                          border: isSelected ? "5px solid #5B7CFF" : "2px solid rgba(255,255,255,0.15)",
                          background: "transparent",
                          transition: "all 0.15s", flexShrink: 0
                        }} />
                        <span style={{ fontSize: 12, color: isSelected ? "#fff" : "rgba(255,255,255,0.55)", fontStyle: "italic" }}>
                          Nenhum (sem atribuição)
                        </span>
                      </div>
                    );
                  })()}
                  {aiUsers.map(function (u) {
                    var isSelected = nodeData.aiUserId === u.id || nodeData.aiUserId === String(u.id);
                    var initials = (u.name || "?")
                      .split(" ")
                      .map(function (p) { return p.charAt(0); })
                      .join("")
                      .slice(0, 2)
                      .toUpperCase();
                    return (
                      <div
                        key={u.id}
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "8px 12px", cursor: "pointer",
                          background: isSelected ? "rgba(91,124,255,0.12)" : "transparent",
                          borderBottom: "1px solid rgba(255,255,255,0.04)",
                          transition: "background 0.15s"
                        }}
                        onClick={function () { updateNodeData("aiUserId", u.id); }}
                      >
                        <div style={{
                          width: 18, height: 18, borderRadius: "50%",
                          border: isSelected ? "5px solid #5B7CFF" : "2px solid rgba(255,255,255,0.15)",
                          background: "transparent",
                          transition: "all 0.15s", flexShrink: 0
                        }} />
                        <div style={{
                          width: 26, height: 26, borderRadius: "50%",
                          background: "linear-gradient(135deg, #8b5cf6 0%, #5B7CFF 100%)",
                          color: "#fff", fontSize: 10, fontWeight: 700,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                          backgroundImage: u.profileImage ? "url(" + u.profileImage + ")" : undefined,
                          backgroundSize: "cover",
                          backgroundPosition: "center"
                        }}>
                          {!u.profileImage && initials}
                        </div>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: isSelected ? "#fff" : "rgba(255,255,255,0.78)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {u.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Prompt */}
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Prompt</span>
                <Tooltip
                  title="Instrucoes que definem o comportamento da IA. Descreva a personalidade, regras, tom de voz e como ela deve responder ao cliente."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>
              <div className={classes.configTextareaWrap}>
                <Tooltip title="Expandir / Editar" placement="top">
                  <div
                    className={classes.configExpandBtn}
                    onClick={function () { setPromptModalOpen(true); }}
                  >
                    <Maximize2 size={12} />
                  </div>
                </Tooltip>
                {/* Prompt com highlight de tags */}
                {(function () {
                  var promptVal = nodeData.prompt || "";
                  var tagColorMap = {};
                  var agentIdForTags = selectedNode.id;
                  var toolIdsForTags = edges
                    .filter(function (e) { return e.source === agentIdForTags && e.sourceHandle === "tools"; })
                    .map(function (e) { return e.target; });
                  nodes.forEach(function (n) {
                    if (toolIdsForTags.indexOf(n.id) === -1) return;
                    if (n.data.nodeType === "tool_files") {
                      (n.data.files || []).forEach(function (f) {
                        tagColorMap["{{" + f.name + "}}"] = "#f59e0b";
                        tagColorMap["{{" + (f.originalName || f.name) + "}}"] = "#f59e0b";
                      });
                    }
                    if (n.data.nodeType === "tool_memory") {
                      (n.data.memories || []).forEach(function (m) {
                        tagColorMap["{{memoria_" + m.name.replace(/\s+/g, "_") + "}}"] = "#ec4899";
                      });
                    }
                    if (n.data.nodeType === "tool_crm_create") {
                      tagColorMap["{{crm_criar_card}}"] = "#10b981";
                      (n.data.fields || []).forEach(function (f) {
                        tagColorMap["{{crm_campo_" + f.name.replace(/\s+/g, "_") + "}}"] = "#10b981";
                      });
                    }
                    if (n.data.nodeType === "tool_crm_update") {
                      tagColorMap["{{crm_atualizar_card}}"] = "#06b6d4";
                    }
                    if (n.data.nodeType === "tool_crm_status") {
                      tagColorMap["{{crm_mover_card}}"] = "#f97316";
                    }
                  });

                  var hasTags = /\{\{[^}]+\}\}/.test(promptVal);

                  if (!hasTags) {
                    return (
                      <textarea
                        className={classes.configTextarea}
                        value={promptVal}
                        onChange={function (e) { updateNodeData("prompt", e.target.value); }}
                        placeholder={"Voce e um assistente virtual da empresa X.\nSeja educado, objetivo e responda apenas sobre nossos produtos e servicos."}
                        style={{ paddingRight: 40 }}
                      />
                    );
                  }

                  // Renderizar preview com tags coloridas
                  var parts = [];
                  var regex = /\{\{[^}]+\}\}/g;
                  var lastIdx = 0;
                  var match;
                  while ((match = regex.exec(promptVal)) !== null) {
                    if (match.index > lastIdx) {
                      parts.push({ text: promptVal.substring(lastIdx, match.index), isTag: false });
                    }
                    parts.push({ text: match[0], isTag: true });
                    lastIdx = match.index + match[0].length;
                  }
                  if (lastIdx < promptVal.length) {
                    parts.push({ text: promptVal.substring(lastIdx), isTag: false });
                  }

                  return (
                    <div
                      onClick={function () { setPromptModalOpen(true); }}
                      style={{
                        padding: "10px 40px 10px 12px",
                        fontSize: 12, lineHeight: 1.6,
                        color: "rgba(255,255,255,0.75)",
                        whiteSpace: "pre-wrap", wordWrap: "break-word",
                        maxHeight: 200, overflowY: "auto",
                        cursor: "text",
                        background: "rgba(255,255,255,0.04)",
                        borderRadius: 8,
                        border: "1px solid rgba(255,255,255,0.08)",
                        transition: "border-color 0.2s"
                      }}
                    >
                      {parts.map(function (part, i) {
                        if (!part.isTag) return React.createElement("span", { key: i }, part.text);
                        var tagColor = tagColorMap[part.text] || "#5B7CFF";
                        return React.createElement("span", {
                          key: i,
                          style: {
                            display: "inline",
                            background: tagColor + "20",
                            border: "1px solid " + tagColor + "45",
                            borderRadius: 4, padding: "2px 6px",
                            color: tagColor, fontWeight: 600,
                            fontSize: 11, lineHeight: 2
                          }
                        }, part.text);
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Recursos conectados (badges estilo n8n) */}
              {(function () {
                var agentId = selectedNode.id;
                var toolIds = edges
                  .filter(function (e) { return e.source === agentId && e.sourceHandle === "tools"; })
                  .map(function (e) { return e.target; });
                if (toolIds.length === 0) return null;

                var badges = [];
                nodes.forEach(function (n) {
                  if (toolIds.indexOf(n.id) === -1) return;
                  if (n.data.nodeType === "tool_files") {
                    var files = n.data.files || [];
                    files.forEach(function (f) {
                      badges.push({ label: f.name || f.originalName, icon: "file", color: "#f59e0b" });
                    });
                  }
                  if (n.data.nodeType === "tool_memory") {
                    var mems = n.data.memories || [];
                    mems.forEach(function (m) {
                      badges.push({ label: m.name, icon: "brain", color: "#ec4899" });
                    });
                  }
                  if (n.data.nodeType === "tool_websearch") {
                    badges.push({ label: "Web Search", icon: "globe", color: "#06b6d4" });
                  }
                  if (n.data.nodeType === "tool_crm_create") {
                    badges.push({ label: "Criar Card", icon: "kanban", color: "#10b981" });
                    var fields = n.data.fields || [];
                    fields.forEach(function (f) {
                      badges.push({ label: f.name, icon: "kanban", color: "#10b981" });
                    });
                  }
                  if (n.data.nodeType === "tool_crm_update") {
                    badges.push({ label: "Atualizar Card", icon: "kanban", color: "#06b6d4" });
                  }
                  if (n.data.nodeType === "tool_crm_status") {
                    badges.push({ label: "Status Card", icon: "kanban", color: "#f97316" });
                  }
                });
                if (badges.length === 0) return null;

                var iconMap = {
                  file: FileUp,
                  brain: Brain,
                  globe: Globe,
                  kanban: SquareKanban,
                };

                return (
                  <div style={{
                    display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8,
                    padding: "8px 10px", borderRadius: 8,
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.06)"
                  }}>
                    <div style={{
                      width: "100%", fontSize: 10, color: "rgba(255,255,255,0.35)",
                      marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5
                    }}>
                      Recursos conectados
                    </div>
                    {badges.map(function (b, i) {
                      var BadgeIcon = iconMap[b.icon] || FileUp;
                      return (
                        <div key={i} style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          padding: "3px 8px 3px 6px", borderRadius: 6,
                          background: b.color + "18",
                          border: "1px solid " + b.color + "30",
                          fontSize: 11, color: b.color,
                          lineHeight: 1.3, maxWidth: "100%"
                        }}>
                          <BadgeIcon size={11} strokeWidth={2.5} style={{ flexShrink: 0 }} />
                          <span style={{
                            overflow: "hidden", textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}>
                            {b.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Memoria */}
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Memoria</span>
                <Tooltip
                  title="Quantidade de mensagens anteriores da conversa que a IA vai considerar para formular a resposta. Quanto maior, mais contexto a IA tera, porem maior o custo de tokens."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>
              <div className={classes.configSliderRow}>
                <input
                  type="range"
                  className={classes.configSlider}
                  min={1}
                  max={100}
                  step={1}
                  value={nodeData.contextWindow || 20}
                  onChange={function (e) {
                    updateNodeData("contextWindow", parseInt(e.target.value, 10));
                  }}
                />
                <span className={classes.configSliderValue}>
                  {nodeData.contextWindow || 20}
                </span>
              </div>
            </div>

            {/* Tempo de espera */}
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Tempo de espera</span>
                <Tooltip
                  title="Tempo em segundos que a IA aguarda apos a ultima mensagem do cliente antes de responder. Se o cliente enviar outra mensagem dentro desse tempo, a contagem reinicia. Isso permite que o cliente envie varias mensagens seguidas antes da IA processar tudo junto."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>
              <div className={classes.configSliderRow}>
                <input
                  type="range"
                  className={classes.configSlider}
                  min={3}
                  max={60}
                  step={1}
                  value={nodeData.waitTime || 12}
                  onChange={function (e) {
                    updateNodeData("waitTime", parseInt(e.target.value, 10));
                  }}
                />
                <span className={classes.configSliderValue}>
                  {nodeData.waitTime || 12}s
                </span>
              </div>
            </div>

            {/* Estilo de resposta */}
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Estilo de resposta</span>
                <Tooltip
                  title="Define o tamanho e nivel de detalhe das respostas da IA."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>
              <div className={classes.configOptionGroup}>
                <div
                  className={classes.configOptionBtn + ((nodeData.responseStyle || "short") === "short" ? " " + classes.configOptionBtnActive : "")}
                  onClick={function () { updateNodeData("responseStyle", "short"); }}
                >
                  <span className={classes.configOptionIcon}>&#9889;</span>
                  Curta
                  <span className={classes.configOptionDesc}>Resumida e direta</span>
                </div>
                <div
                  className={classes.configOptionBtn + ((nodeData.responseStyle) === "medium" ? " " + classes.configOptionBtnActive : "")}
                  onClick={function () { updateNodeData("responseStyle", "medium"); }}
                >
                  <span className={classes.configOptionIcon}>&#9997;&#65039;</span>
                  Media
                  <span className={classes.configOptionDesc}>Equilibrada e clara</span>
                </div>
                <div
                  className={classes.configOptionBtn + ((nodeData.responseStyle) === "detailed" ? " " + classes.configOptionBtnActive : "")}
                  onClick={function () { updateNodeData("responseStyle", "detailed"); }}
                >
                  <span className={classes.configOptionIcon}>&#128214;</span>
                  Detalhada
                  <span className={classes.configOptionDesc}>Completa e explicativa</span>
                </div>
              </div>
            </div>

            {/* Enviar em blocos */}
            <div className={classes.configField}>
              <div className={classes.configLabelRow}>
                <span className={classes.configLabel}>Mensagens em blocos</span>
                <Tooltip
                  title="Quando ativado, a IA quebra respostas longas em multiplos baloes de mensagem, simulando uma conversa mais natural. Ex: em vez de um unico texto longo, envia cada frase ou paragrafo como mensagem separada."
                  placement="top"
                  classes={{ tooltip: classes.configInfoTooltip }}
                >
                  <span className={classes.configInfoIcon}>
                    <Info size={13} />
                  </span>
                </Tooltip>
              </div>
              <div className={classes.configOptionGroup}>
                <div
                  className={classes.configOptionBtn + (!(nodeData.splitMessages) ? " " + classes.configOptionBtnActive : "")}
                  onClick={function () { updateNodeData("splitMessages", false); }}
                >
                  <span className={classes.configOptionIcon}>&#128172;</span>
                  Unica
                  <span className={classes.configOptionDesc}>Uma mensagem so</span>
                </div>
                <div
                  className={classes.configOptionBtn + ((nodeData.splitMessages) ? " " + classes.configOptionBtnActive : "")}
                  onClick={function () { updateNodeData("splitMessages", true); }}
                >
                  <span className={classes.configOptionIcon}>&#128172;&#128172;</span>
                  Em blocos
                  <span className={classes.configOptionDesc}>Varios baloes separados</span>
                </div>
              </div>
            </div>

            {/* ── Separador: Transferencia ─────────── */}
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", margin: "16px 0 12px" }} />

            {/* Toggle: Transferir para atendente */}
            <div className={classes.configField}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={classes.configLabel}>Transferir para atendente</span>
                  <Tooltip
                    title="A IA vai analisar a conversa e transferir automaticamente para o atendente selecionado quando identificar a situacao descrita."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div
                  style={{
                    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                    background: nodeData.enableEscalation ? "#5B7CFF" : "rgba(255,255,255,0.1)",
                    position: "relative", transition: "background 0.2s"
                  }}
                  onClick={function () {
                    updateNodeData("enableEscalation", !nodeData.enableEscalation);
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 8, background: "#fff",
                    position: "absolute", top: 2, transition: "left 0.2s",
                    left: nodeData.enableEscalation ? 18 : 2
                  }} />
                </div>
              </div>
            </div>

            {/* Conteudo de escalation (visivel somente quando enableEscalation) */}
            {nodeData.enableEscalation && (function () {
              // Resolve lista de atendentes selecionados aceitando legado
              // (escalateToUserId singular) para nao quebrar fluxos antigos.
              var selectedIds = (function () {
                var fromArr = Array.isArray(nodeData.escalateToUserIds) ? nodeData.escalateToUserIds : [];
                if (fromArr.length > 0) return fromArr;
                return nodeData.escalateToUserId ? [nodeData.escalateToUserId] : [];
              })();
              var escalationMode = ["random", "balanced", "percentage", "context"].indexOf(nodeData.escalationMode) >= 0
                ? nodeData.escalationMode
                : "random";
              var escalationPercentages = (nodeData.escalationPercentages && typeof nodeData.escalationPercentages === "object")
                ? nodeData.escalationPercentages
                : {};
              var escalationContextMap = (nodeData.escalationContextMap && typeof nodeData.escalationContextMap === "object")
                ? nodeData.escalationContextMap
                : {};
              var contextEntries = Object.keys(escalationContextMap).map(function (tag) {
                return { tag: tag, userId: escalationContextMap[tag] };
              });
              var percentTotal = selectedIds.reduce(function (sum, id) {
                var v = escalationPercentages[String(id)] || escalationPercentages[id] || 0;
                return sum + (typeof v === "number" ? v : 0);
              }, 0);

              var toggleUser = function (u) {
                var isSelected = selectedIds.indexOf(u.id) >= 0;
                var next = isSelected
                  ? selectedIds.filter(function (id) { return id !== u.id; })
                  : selectedIds.concat([u.id]);
                updateNodeData("escalateToUserIds", next);
                // Limpa legado para evitar inconsistencia
                if (nodeData.escalateToUserId) {
                  updateNodeData("escalateToUserId", null);
                }
                // Se desmarcou, remove o percentual associado
                if (isSelected && escalationPercentages.hasOwnProperty(String(u.id))) {
                  var cleaned = {};
                  Object.keys(escalationPercentages).forEach(function (k) {
                    if (k !== String(u.id)) cleaned[k] = escalationPercentages[k];
                  });
                  updateNodeData("escalationPercentages", cleaned);
                }
                // Se desmarcou, remove entradas órfãs do mapa de contexto
                if (isSelected) {
                  var cleanedCtx = {};
                  var dirty = false;
                  Object.keys(escalationContextMap).forEach(function (tag) {
                    if (escalationContextMap[tag] !== u.id) {
                      cleanedCtx[tag] = escalationContextMap[tag];
                    } else {
                      dirty = true;
                    }
                  });
                  if (dirty) {
                    updateNodeData("escalationContextMap", cleanedCtx);
                  }
                }
              };

              return (
              <React.Fragment>
                {/* Seletor de atendentes (multiplo) */}
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Atendentes destino</span>
                    <Tooltip
                      title="Selecione um ou mais atendentes. Se marcar 2 ou mais, escolha um modo de distribuição abaixo."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  <div style={{
                    maxHeight: 160, overflowY: "auto", marginTop: 6,
                    borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)"
                  }}>
                    {users.length === 0 && (
                      <div style={{ padding: 12, fontSize: 11, color: "rgba(255,255,255,0.4)", textAlign: "center" }}>
                        Nenhum usuário encontrado
                      </div>
                    )}
                    {users.map(function (u) {
                      var isSelected = selectedIds.indexOf(u.id) >= 0;
                      return (
                        <div
                          key={u.id}
                          style={{
                            display: "flex", alignItems: "center", gap: 8,
                            padding: "8px 12px", cursor: "pointer",
                            background: isSelected ? "rgba(91,124,255,0.12)" : "transparent",
                            borderBottom: "1px solid rgba(255,255,255,0.04)",
                            transition: "background 0.15s"
                          }}
                          onClick={function () { toggleUser(u); }}
                        >
                          <div style={{
                            width: 18, height: 18, borderRadius: 4,
                            border: isSelected ? "2px solid #5B7CFF" : "2px solid rgba(255,255,255,0.15)",
                            background: isSelected ? "#5B7CFF" : "transparent",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            transition: "all 0.15s", flexShrink: 0
                          }}>
                            {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                          </div>
                          <span style={{ fontSize: 12, color: isSelected ? "#fff" : "rgba(255,255,255,0.7)" }}>
                            {u.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Modo de distribuição (aparece com 2+ atendentes) */}
                {selectedIds.length >= 2 && (
                  <div className={classes.configField}>
                    <div className={classes.configLabelRow}>
                      <span className={classes.configLabel}>Modo de distribuição</span>
                      <Tooltip
                        title="Como a IA escolhe entre os atendentes selecionados a cada transferência."
                        placement="top"
                        classes={{ tooltip: classes.configInfoTooltip }}
                      >
                        <span className={classes.configInfoIcon}>
                          <Info size={13} />
                        </span>
                      </Tooltip>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                      {[
                        { value: "random",     label: "Aleatório",  desc: "Sorteia um atendente a cada transferência" },
                        { value: "balanced",   label: "Balanceado", desc: "Escolhe quem tem menos tickets em atendimento agora" },
                        { value: "percentage", label: "Percentual", desc: "Distribui conforme % configurado abaixo" },
                        { value: "context",    label: "Por contexto", desc: "Roteia conforme tag definida no prompt (fallback balanceado)" },
                      ].map(function (opt) {
                        var isOn = escalationMode === opt.value;
                        return (
                          <div
                            key={opt.value}
                            onClick={function () { updateNodeData("escalationMode", opt.value); }}
                            style={{
                              display: "flex", alignItems: "flex-start", gap: 8,
                              padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                              background: isOn ? "rgba(91,124,255,0.12)" : "rgba(255,255,255,0.02)",
                              border: isOn ? "1px solid rgba(91,124,255,0.35)" : "1px solid rgba(255,255,255,0.06)",
                              transition: "all 0.15s",
                            }}
                          >
                            <div style={{
                              width: 14, height: 14, borderRadius: "50%",
                              border: isOn ? "4px solid #5B7CFF" : "2px solid rgba(255,255,255,0.20)",
                              background: isOn ? "#fff" : "transparent",
                              marginTop: 2, flexShrink: 0,
                              transition: "all 0.15s",
                            }} />
                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: isOn ? "#fff" : "rgba(255,255,255,0.78)" }}>
                                {opt.label}
                              </span>
                              <span style={{ fontSize: 10, color: "rgba(255,255,255,0.45)" }}>{opt.desc}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Mapa tag → atendente (aparece com mode=context) */}
                {selectedIds.length >= 2 && escalationMode === "context" && (
                  <div className={classes.configField}>
                    <div className={classes.configLabelRow}>
                      <span className={classes.configLabel}>Mapa de roteamento por contexto</span>
                      <Tooltip
                        title="Cada tag aponta para 1 atendente. Use a tag no prompt: transferir_para_atendente(motivo='...', contexto='nome-da-tag')."
                        placement="top"
                        classes={{ tooltip: classes.configInfoTooltip }}
                      >
                        <span className={classes.configInfoIcon}>
                          <Info size={13} />
                        </span>
                      </Tooltip>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      {contextEntries.length === 0 && (
                        <div style={{
                          padding: "10px 12px", fontSize: 11,
                          color: "rgba(255,255,255,0.4)", textAlign: "center",
                          border: "1px dashed rgba(255,255,255,0.08)", borderRadius: 6
                        }}>
                          Nenhuma tag configurada — adicione ao menos uma para roteamento contextual.
                        </div>
                      )}
                      {contextEntries.map(function (entry, idx) {
                        var duplicated = contextEntries.filter(function (e) {
                          return e.tag.trim() && e.tag === entry.tag;
                        }).length > 1;
                        var emptyTag = entry.tag.trim().length === 0;
                        var hasWarning = duplicated || emptyTag;
                        return (
                          <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <input
                              className={classes.configInput}
                              type="text"
                              placeholder="tag-em-kebab-case"
                              value={entry.tag}
                              onChange={function (e) {
                                var newTag = e.target.value;
                                var next = {};
                                contextEntries.forEach(function (it, i) {
                                  var key = i === idx ? newTag : it.tag;
                                  if (key !== undefined) {
                                    next[key] = it.userId;
                                  }
                                });
                                updateNodeData("escalationContextMap", next);
                              }}
                              style={{
                                flex: 1,
                                borderColor: hasWarning ? "rgba(245,158,11,0.6)" : undefined
                              }}
                            />
                            <select
                              className={classes.configInput}
                              value={entry.userId}
                              onChange={function (e) {
                                var newUserId = parseInt(e.target.value, 10);
                                var next = Object.assign({}, escalationContextMap);
                                next[entry.tag] = newUserId;
                                updateNodeData("escalationContextMap", next);
                              }}
                              style={{ flex: 1 }}
                            >
                              {selectedIds.map(function (uid) {
                                var user = users.find(function (u) { return u.id === uid; });
                                return (
                                  <option key={uid} value={uid}>
                                    {user ? user.name : ("ID " + uid)}
                                  </option>
                                );
                              })}
                            </select>
                            <button
                              type="button"
                              onClick={function () {
                                var next = {};
                                contextEntries.forEach(function (it, i) {
                                  if (i !== idx) next[it.tag] = it.userId;
                                });
                                updateNodeData("escalationContextMap", next);
                              }}
                              style={{
                                background: "transparent",
                                border: "1px solid rgba(255,255,255,0.12)",
                                borderRadius: 6,
                                color: "rgba(255,255,255,0.5)",
                                padding: "6px 8px",
                                cursor: "pointer",
                                fontSize: 12
                              }}
                              title="Remover esta tag"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        onClick={function () {
                          // Sugere nova tag única — chave vazia inicial,
                          // operador renomeia em seguida.
                          var seedTag = "";
                          var i = 0;
                          while (escalationContextMap.hasOwnProperty(seedTag)) {
                            i += 1;
                            seedTag = "nova-tag-" + i;
                          }
                          var next = Object.assign({}, escalationContextMap);
                          next[seedTag] = selectedIds[0];
                          updateNodeData("escalationContextMap", next);
                        }}
                        style={{
                          marginTop: 4,
                          background: "rgba(91,124,255,0.10)",
                          border: "1px solid rgba(91,124,255,0.30)",
                          borderRadius: 6,
                          color: "#5B7CFF",
                          padding: "6px 10px",
                          cursor: "pointer",
                          fontSize: 12,
                          fontWeight: 600
                        }}
                      >
                        + Adicionar tag
                      </button>
                      {(contextEntries.some(function (e) { return e.tag.trim().length === 0; }) ||
                        contextEntries.some(function (e, i) {
                          return e.tag.trim() && contextEntries.filter(function (x) { return x.tag === e.tag; }).length > 1;
                        })) && (
                        <div style={{
                          marginTop: 6, padding: "6px 8px", borderRadius: 6,
                          fontSize: 11, fontWeight: 600,
                          color: "#f59e0b",
                          background: "rgba(245,158,11,0.06)",
                          border: "1px solid rgba(245,158,11,0.18)"
                        }}>
                          Há tags vazias ou duplicadas — corrija para que o roteamento funcione.
                        </div>
                      )}
                      <div style={{
                        marginTop: 8, padding: "6px 8px", borderRadius: 6,
                        fontSize: 11, color: "rgba(255,255,255,0.55)",
                        background: "rgba(255,255,255,0.02)",
                        border: "1px solid rgba(255,255,255,0.05)"
                      }}>
                        No prompt, instrua a IA: <code style={{ color: "rgba(255,255,255,0.75)" }}>transferir_para_atendente(motivo='...', contexto='nome-da-tag')</code>. Quando a IA omitir <code>contexto</code>, a transferência cai no balanceamento padrão.
                      </div>
                    </div>
                  </div>
                )}

                {/* Inputs de percentual (aparece com mode=percentage) */}
                {selectedIds.length >= 2 && escalationMode === "percentage" && (
                  <div className={classes.configField}>
                    <div className={classes.configLabelRow}>
                      <span className={classes.configLabel}>Percentuais</span>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      {selectedIds.map(function (id) {
                        var user = users.find(function (u) { return u.id === id; });
                        var pct = (escalationPercentages[String(id)] || escalationPercentages[id] || 0);
                        return (
                          <div key={id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <span style={{ flex: 1, fontSize: 12, color: "rgba(255,255,255,0.78)" }}>
                              {user ? user.name : ("ID " + id)}
                            </span>
                            <input
                              className={classes.configInput}
                              type="number"
                              min="0"
                              max="100"
                              step="5"
                              value={pct}
                              onChange={function (e) {
                                var val = parseInt(e.target.value, 10);
                                if (isNaN(val)) val = 0;
                                if (val < 0) val = 0;
                                if (val > 100) val = 100;
                                var next = Object.assign({}, escalationPercentages);
                                next[String(id)] = val;
                                updateNodeData("escalationPercentages", next);
                              }}
                              style={{ width: 70, textAlign: "right" }}
                            />
                            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>%</span>
                          </div>
                        );
                      })}
                      <div style={{
                        marginTop: 6, padding: "6px 8px", borderRadius: 6,
                        fontSize: 11, fontWeight: 600,
                        color: percentTotal === 100 ? "#22c55e" : "#f59e0b",
                        background: percentTotal === 100 ? "rgba(34,197,94,0.06)" : "rgba(245,158,11,0.06)",
                        border: percentTotal === 100 ? "1px solid rgba(34,197,94,0.18)" : "1px solid rgba(245,158,11,0.18)",
                      }}>
                        Total: {percentTotal}% {percentTotal !== 100 && "— ajuste para somar 100%"}
                      </div>
                    </div>
                  </div>
                )}

                {/* Condicao de transferencia */}
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Quando transferir?</span>
                    <Tooltip
                      title="Descreva em linguagem natural as situacoes em que a IA deve transferir para o atendente. Ex: quando o cliente estiver irritado, quiser reclamar, pedir para falar com humano, etc."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  <div style={{ position: "relative" }}>
                    <textarea
                      className={classes.configTextarea}
                      value={nodeData.escalationCondition || ""}
                      onChange={function (e) {
                        updateNodeData("escalationCondition", e.target.value);
                      }}
                      placeholder={"Transferir para o atendente quando:\n- O cliente demonstrar irritacao ou insatisfacao\n- O cliente pedir para falar com um humano\n- O cliente quiser fazer uma reclamacao"}
                      style={{ minHeight: 90, paddingRight: 40 }}
                    />
                    <Tooltip title="Reformular com IA" placement="top">
                      <div
                        style={{
                          position: "absolute", top: 8, right: 8,
                          width: 28, height: 28, borderRadius: 6,
                          background: nodeData._reformulating ? "rgba(91,124,255,0.3)" : "rgba(91,124,255,0.15)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: nodeData._reformulating ? "wait" : "pointer",
                          transition: "background 0.2s"
                        }}
                        onClick={function () {
                          if (nodeData._reformulating) return;
                          var text = nodeData.escalationCondition;
                          if (!text || !text.trim()) return;
                          updateNodeData("_reformulating", true);
                          api.post("/ai-agents/reformulate-prompt", {
                            prompt: "Reformule o seguinte texto de instrucao de transferencia para ficar mais claro e estruturado para uma IA de atendimento entender exatamente quando deve transferir o cliente para um atendente humano. Mantenha em portugues, seja objetivo:\n\n" + text
                          })
                          .then(function (res) {
                            if (res.data && res.data.reformulated) {
                              updateNodeData("escalationCondition", res.data.reformulated);
                            }
                          })
                          .catch(function () {})
                          .finally(function () {
                            updateNodeData("_reformulating", false);
                          });
                        }}
                      >
                        {nodeData._reformulating
                          ? <RefreshCw size={13} color="#5B7CFF" style={{ animation: "spin 1s linear infinite" }} />
                          : <Sparkles size={13} color="#5B7CFF" />
                        }
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </React.Fragment>
              );
            })()}

            {/* ── Separador: Encerramento automático ─────────── */}
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", margin: "16px 0 12px" }} />

            {/* Toggle: Permitir IA encerrar atendimento */}
            <div className={classes.configField}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={classes.configLabel}>Encerrar atendimento</span>
                  <Tooltip
                    title="Quando ativo, a IA pode encerrar o ticket sozinha quando o cliente confirmar explicitamente que não precisa de mais nada (ex: 'só isso', 'obrigado', 'pode encerrar')."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div
                  style={{
                    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                    background: nodeData.enableAutoResolve ? "#5B7CFF" : "rgba(255,255,255,0.1)",
                    position: "relative", transition: "background 0.2s"
                  }}
                  onClick={function () {
                    updateNodeData("enableAutoResolve", !nodeData.enableAutoResolve);
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 8, background: "#fff",
                    position: "absolute", top: 2, transition: "left 0.2s",
                    left: nodeData.enableAutoResolve ? 18 : 2
                  }} />
                </div>
              </div>
              {nodeData.enableAutoResolve && (
                <div style={{
                  marginTop: 8, padding: "8px 10px", borderRadius: 6,
                  background: "rgba(91,124,255,0.06)",
                  border: "1px solid rgba(91,124,255,0.18)",
                  fontSize: 10, lineHeight: 1.4,
                  color: "rgba(255,255,255,0.6)",
                }}>
                  A IA enviará uma despedida curta e fechará o ticket automaticamente quando o cliente confirmar que não precisa de mais nada.
                </div>
              )}
            </div>

            {/* ── Separador: Tools avancadas ─────────── */}
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", margin: "16px 0 12px" }} />

            {/* Toggle: Transferir para fila */}
            <div className={classes.configField}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={classes.configLabel}>Transferir para fila</span>
                  <Tooltip
                    title="Quando ativo, a IA pode transferir o ticket para uma FILA inteira (sem escolher atendente específico). A lista de filas disponíveis é enviada automaticamente para a IA — ela escolhe pelo nome."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div
                  style={{
                    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                    background: nodeData.enableTransferToQueue ? "#5B7CFF" : "rgba(255,255,255,0.1)",
                    position: "relative", transition: "background 0.2s"
                  }}
                  onClick={function () {
                    updateNodeData("enableTransferToQueue", !nodeData.enableTransferToQueue);
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 8, background: "#fff",
                    position: "absolute", top: 2, transition: "left 0.2s",
                    left: nodeData.enableTransferToQueue ? 18 : 2
                  }} />
                </div>
              </div>
              {nodeData.enableTransferToQueue && (
                <div style={{
                  marginTop: 8, padding: "8px 10px", borderRadius: 6,
                  background: "rgba(91,124,255,0.06)",
                  border: "1px solid rgba(91,124,255,0.18)",
                  fontSize: 10, lineHeight: 1.4,
                  color: "rgba(255,255,255,0.6)",
                }}>
                  Todas as filas ativas da empresa serão oferecidas à IA. Útil para casos como "vou direcionar para a equipe Comercial".
                </div>
              )}
            </div>

            {/* Toggle: Etiquetar atendimento */}
            <div className={classes.configField}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={classes.configLabel}>Etiquetar atendimento</span>
                  <Tooltip
                    title="Quando ativo, a IA pode aplicar etiquetas (tags) ao ticket para categorização e analytics. Apenas etiquetas cadastradas na empresa serão utilizadas — nomes inexistentes são ignorados."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div
                  style={{
                    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                    background: nodeData.enableTagTicket ? "#5B7CFF" : "rgba(255,255,255,0.1)",
                    position: "relative", transition: "background 0.2s"
                  }}
                  onClick={function () {
                    updateNodeData("enableTagTicket", !nodeData.enableTagTicket);
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 8, background: "#fff",
                    position: "absolute", top: 2, transition: "left 0.2s",
                    left: nodeData.enableTagTicket ? 18 : 2
                  }} />
                </div>
              </div>
              {nodeData.enableTagTicket && (
                <div style={{
                  marginTop: 8, padding: "8px 10px", borderRadius: 6,
                  background: "rgba(91,124,255,0.06)",
                  border: "1px solid rgba(91,124,255,0.18)",
                  fontSize: 10, lineHeight: 1.4,
                  color: "rgba(255,255,255,0.6)",
                }}>
                  A IA receberá a lista de etiquetas cadastradas em Tags. Ação silenciosa — o cliente não percebe.
                </div>
              )}
            </div>

            {/* Toggle: Adicionar nota interna */}
            <div className={classes.configField}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={classes.configLabel}>Adicionar nota interna</span>
                  <Tooltip
                    title="Quando ativo, a IA pode registrar notas internas no ticket (visíveis apenas para atendentes). Útil para deixar contexto antes de transferir o atendimento."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div
                  style={{
                    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                    background: nodeData.enableAddNote ? "#5B7CFF" : "rgba(255,255,255,0.1)",
                    position: "relative", transition: "background 0.2s"
                  }}
                  onClick={function () {
                    updateNodeData("enableAddNote", !nodeData.enableAddNote);
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 8, background: "#fff",
                    position: "absolute", top: 2, transition: "left 0.2s",
                    left: nodeData.enableAddNote ? 18 : 2
                  }} />
                </div>
              </div>
              {nodeData.enableAddNote && (
                <div style={{
                  marginTop: 8, padding: "8px 10px", borderRadius: 6,
                  background: "rgba(91,124,255,0.06)",
                  border: "1px solid rgba(91,124,255,0.18)",
                  fontSize: 10, lineHeight: 1.4,
                  color: "rgba(255,255,255,0.6)",
                }}>
                  A IA deixará um resumo estruturado antes de transferir, dando contexto ao atendente. Cliente não recebe a nota.
                </div>
              )}
            </div>

            {/* Toggle: Agendamento Trinks */}
            <div className={classes.configField}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={classes.configLabel}>Agendamento Trinks</span>
                  <Tooltip
                    title="Quando ativo, a IA pode consultar serviços e horários e criar/cancelar/remarcar agendamentos no sistema Trinks do salão. Requer uma integração Trinks ativa em Integrações."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div
                  style={{
                    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                    background: nodeData.enableTrinks ? "#5B7CFF" : "rgba(255,255,255,0.1)",
                    position: "relative", transition: "background 0.2s"
                  }}
                  onClick={function () {
                    updateNodeData("enableTrinks", !nodeData.enableTrinks);
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 8, background: "#fff",
                    position: "absolute", top: 2, transition: "left 0.2s",
                    left: nodeData.enableTrinks ? 18 : 2
                  }} />
                </div>
              </div>
              {nodeData.enableTrinks && (
                <div style={{
                  marginTop: 8, padding: "8px 10px", borderRadius: 6,
                  background: "rgba(91,124,255,0.06)",
                  border: "1px solid rgba(91,124,255,0.18)",
                  fontSize: 10, lineHeight: 1.4,
                  color: "rgba(255,255,255,0.6)",
                }}>
                  A IA consultará e agendará no salão via Trinks. Configure uma integração Trinks ativa em <strong>Integrações</strong> para que funcione.
                </div>
              )}
            </div>

            {/* Separador: Processamento de Mídia */}
            <div style={{
              borderTop: "1px solid rgba(255,255,255,0.06)",
              margin: "16px 0 12px",
              paddingTop: 12,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.5,
              color: "rgba(255,255,255,0.7)",
              textTransform: "uppercase",
            }}>
              Processamento de Mídia
            </div>

            {/* Toggle: Processar imagens */}
            <div className={classes.configField}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={classes.configLabel}>Processar imagens recebidas</span>
                  <Tooltip
                    title="Quando ativo, fotos enviadas pelo cliente são analisadas pelo modelo de visão (custo extra). Quando inativo, a IA recebe apenas '[O cliente enviou uma imagem...]' como texto e pede para o cliente descrever."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div
                  style={{
                    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                    background: nodeData.processImages ? "#5B7CFF" : "rgba(255,255,255,0.1)",
                    position: "relative", transition: "background 0.2s"
                  }}
                  onClick={function () {
                    updateNodeData("processImages", !nodeData.processImages);
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 8, background: "#fff",
                    position: "absolute", top: 2, transition: "left 0.2s",
                    left: nodeData.processImages ? 18 : 2
                  }} />
                </div>
              </div>
            </div>

            {/* Toggle: Transcrever áudio */}
            <div className={classes.configField}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={classes.configLabel}>Transcrever áudios recebidos</span>
                  <Tooltip
                    title="Quando ativo, áudios são transcritos automaticamente via Whisper (custo extra). Quando inativo, a IA recebe '[O cliente enviou um áudio. Peça para escrever em texto.]' e orienta o cliente a digitar."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div
                  style={{
                    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                    background: nodeData.processAudio ? "#5B7CFF" : "rgba(255,255,255,0.1)",
                    position: "relative", transition: "background 0.2s"
                  }}
                  onClick={function () {
                    updateNodeData("processAudio", !nodeData.processAudio);
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 8, background: "#fff",
                    position: "absolute", top: 2, transition: "left 0.2s",
                    left: nodeData.processAudio ? 18 : 2
                  }} />
                </div>
              </div>
            </div>

            {/* Toggle: Processar vídeo */}
            <div className={classes.configField}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={classes.configLabel}>Processar vídeos recebidos</span>
                  <Tooltip
                    title="Configuração preparada para análise futura de vídeo. Hoje, vídeos sempre viram '[O cliente enviou um vídeo. Peça para descrever em texto.]' — independente deste toggle."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div
                  style={{
                    width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                    background: nodeData.processVideo ? "#5B7CFF" : "rgba(255,255,255,0.1)",
                    position: "relative", transition: "background 0.2s"
                  }}
                  onClick={function () {
                    updateNodeData("processVideo", !nodeData.processVideo);
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 8, background: "#fff",
                    position: "absolute", top: 2, transition: "left 0.2s",
                    left: nodeData.processVideo ? 18 : 2
                  }} />
                </div>
              </div>
            </div>
          </React.Fragment>
        )}

        {cfg.type === "tool_files" && (function () {
          var files = nodeData.files || [];
          var fileInputRef = React.createRef();

          var MAX_FILE_SIZE = 50 * 1024 * 1024;
          var ACCEPTED_TYPES = "image/*,application/pdf,audio/*";

          var formatFileSize = function (bytes) {
            if (bytes < 1024) return bytes + " B";
            if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
            return (bytes / (1024 * 1024)).toFixed(1) + " MB";
          };

          var handleFileSelected = function (fileObj) {
            if (!fileObj) return;
            if (fileObj.size > MAX_FILE_SIZE) {
              toastError({ response: { data: { error: "Arquivo excede 50MB" } } });
              return;
            }
            var isValid = fileObj.type.startsWith("image/") || fileObj.type === "application/pdf" || fileObj.type.startsWith("audio/");
            if (!isValid) {
              toastError({ response: { data: { error: "Tipo nao suportado. Use imagem, PDF ou audio." } } });
              return;
            }
            setPendingFile(fileObj);
          };

          var handleAdd = function () {
            var name = pendingFileName.trim();
            if (!name) {
              toastError({ response: { data: { error: "Digite um nome para o arquivo" } } });
              return;
            }
            if (!pendingFile) {
              toastError({ response: { data: { error: "Selecione um arquivo" } } });
              return;
            }

            setFileUploading(true);
            var formData = new FormData();
            formData.append("file", pendingFile);
            formData.append("fileName", name);

            api.post("/workflows/upload", formData, {
              headers: { "Content-Type": "multipart/form-data" },
            })
              .then(function (res) {
                var newFile = {
                  id: res.data.id || Date.now() + "-" + Math.random().toString(36).substr(2, 9),
                  name: name,
                  originalName: pendingFile.name,
                  size: pendingFile.size,
                  type: pendingFile.type,
                  url: res.data.url || res.data.path,
                };
                updateNodeData("files", files.concat(newFile));
                setPendingFileName("");
                setPendingFile(null);
              })
              .catch(function (err) {
                toastError(err);
              })
              .finally(function () {
                setFileUploading(false);
              });
          };

          var removeFile = function (fileId) {
            updateNodeData("files", files.filter(function (f) { return f.id !== fileId; }));
          };

          return (
            <React.Fragment>
              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Nome do arquivo</span>
                  <Tooltip
                    title="De um nome unico para identificar este arquivo. Ex: Catalogo 2026, Tabela de precos, etc."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <input
                  className={classes.configInput}
                  value={pendingFileName}
                  onChange={function (e) { setPendingFileName(e.target.value); }}
                  placeholder="ex: Catalogo de produtos 2026"
                  style={{ width: "100%" }}
                />
              </div>

              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Arquivo</span>
                  <Tooltip
                    title="Selecione imagem, PDF ou audio (ate 50MB)."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_TYPES}
                  style={{ display: "none" }}
                  onChange={function (e) {
                    if (e.target.files && e.target.files[0]) {
                      handleFileSelected(e.target.files[0]);
                    }
                    e.target.value = "";
                  }}
                />

                <div
                  className={classes.configUploadZone}
                  onClick={function () { fileInputRef.current && fileInputRef.current.click(); }}
                  style={{ padding: pendingFile ? "12px 16px" : "20px 16px" }}
                >
                  {pendingFile ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                      <div className={classes.configFileIcon}>
                        <FileUp size={13} color="#f59e0b" strokeWidth={2} />
                      </div>
                      <div className={classes.configFileInfo}>
                        <div className={classes.configFileName}>{pendingFile.name}</div>
                        <div className={classes.configFileSize}>{formatFileSize(pendingFile.size)}</div>
                      </div>
                      <span
                        className={classes.configFileRemove}
                        onClick={function (e) { e.stopPropagation(); setPendingFile(null); }}
                      >
                        <X size={14} />
                      </span>
                    </div>
                  ) : (
                    <React.Fragment>
                      <div className={classes.configUploadIcon}>
                        <Upload size={18} color="#f59e0b" strokeWidth={2} />
                      </div>
                      <div className={classes.configUploadText}>
                        Clique para selecionar
                      </div>
                      <div className={classes.configUploadHint}>
                        Imagem, PDF ou audio — ate 50MB
                      </div>
                    </React.Fragment>
                  )}
                </div>
              </div>

              <div className={classes.configField}>
                <button
                  className={classes.saveBtn}
                  onClick={handleAdd}
                  disabled={fileUploading || !pendingFileName.trim() || !pendingFile}
                  style={{ width: "100%", justifyContent: "center" }}
                >
                  {fileUploading ? (
                    <React.Fragment>
                      <CircularProgress size={14} style={{ color: "#fff" }} />
                      Enviando...
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <Plus size={14} />
                      Adicionar arquivo
                    </React.Fragment>
                  )}
                </button>
              </div>

              {files.length > 0 && (
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Arquivos adicionados ({files.length})</span>
                  </div>
                  <div className={classes.configFileList}>
                    {files.map(function (file) {
                      return (
                        <div key={file.id} className={classes.configFileItem}>
                          <div className={classes.configFileIcon}>
                            <FileUp size={13} color="#f59e0b" strokeWidth={2} />
                          </div>
                          <div className={classes.configFileInfo}>
                            <div className={classes.configFileName}>{file.name}</div>
                            <div className={classes.configFileSize}>
                              {file.originalName} — {formatFileSize(file.size)}
                            </div>
                          </div>
                          <span
                            className={classes.configFileRemove}
                            onClick={function () { removeFile(file.id); }}
                          >
                            <Trash2 size={12} />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })()}

        {cfg.type === "tool_memory" && (function () {
          var memories = nodeData.memories || [];
          var editingId = memEditingId;
          var setEditingId = setMemEditingId;
          var expandedId = memExpandedId;
          var setExpandedId = setMemExpandedId;
          var generating = memGenerating;

          var generateSummaryFn = function () {
            if (!memContent.trim()) return;
            setMemGenerating(true);
            api.post("/workflows/generate-summary", { content: memContent })
              .then(function (res) {
                setMemSummary(res.data.summary || "");
              })
              .catch(function () {
                setMemSummary(memContent.substring(0, 120) + (memContent.length > 120 ? "..." : ""));
              })
              .finally(function () {
                setMemGenerating(false);
              });
          };

          var addMemory = function () {
            if (!memName.trim()) {
              toastError({ response: { data: { error: "Digite um nome para a memoria" } } });
              return;
            }
            if (!memContent.trim()) {
              toastError({ response: { data: { error: "Digite o conteudo da memoria" } } });
              return;
            }
            var summary = memSummary.trim() || memContent.substring(0, 120) + (memContent.length > 120 ? "..." : "");

            if (editingId) {
              var updated = memories.map(function (m) {
                if (m.id !== editingId) return m;
                return Object.assign({}, m, { name: memName.trim(), content: memContent.trim(), summary: summary });
              });
              updateNodeData("memories", updated);
              setEditingId(null);
            } else {
              var newMem = {
                id: Date.now() + "-" + Math.random().toString(36).substr(2, 9),
                name: memName.trim(),
                content: memContent.trim(),
                summary: summary,
              };
              updateNodeData("memories", memories.concat(newMem));
            }

            setMemName("");
            setMemContent("");
            setMemSummary("");
          };

          var editMemory = function (mem) {
            setEditingId(mem.id);
            setMemName(mem.name);
            setMemContent(mem.content);
            setMemSummary(mem.summary || "");
          };

          var cancelEdit = function () {
            setEditingId(null);
            setMemName("");
            setMemContent("");
            setMemSummary("");
          };

          var removeMemory = function (memId) {
            updateNodeData("memories", memories.filter(function (m) { return m.id !== memId; }));
            if (editingId === memId) cancelEdit();
          };

          return (
            <React.Fragment>
              {/* Nome da memoria */}
              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>
                    {editingId ? "Editando memoria" : "Nome da memoria"}
                  </span>
                  <Tooltip
                    title="De um nome unico para identificar esta memoria. Ex: Politica de troca, Tabela de precos, Horario de funcionamento."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <input
                  className={classes.configInput}
                  value={memName}
                  onChange={function (e) { setMemName(e.target.value); }}
                  placeholder="ex: Politica de troca e devolucao"
                  style={{ width: "100%" }}
                />
              </div>

              {/* Conteudo do prompt */}
              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Conteudo (prompt)</span>
                  <Tooltip
                    title="Escreva aqui as instrucoes detalhadas para esse caso especifico. A IA vai ler esse conteudo quando identificar que o assunto e relacionado."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div className={classes.configTextareaWrap}>
                  <Tooltip title="Expandir" placement="top">
                    <div
                      className={classes.configExpandBtn}
                      onClick={function () { setMemPromptModalOpen(true); }}
                    >
                      <Maximize2 size={12} />
                    </div>
                  </Tooltip>
                  <textarea
                    className={classes.configTextarea}
                    value={memContent}
                    onChange={function (e) { setMemContent(e.target.value); }}
                    placeholder={"Quando o cliente perguntar sobre troca ou devolucao:\n- Prazo de 7 dias corridos apos o recebimento\n- Produto deve estar sem uso e na embalagem original\n- Enviar foto do produto + nota fiscal\n- Solicitar ao cliente o numero do pedido"}
                    style={{ minHeight: 120, paddingRight: 40 }}
                  />
                </div>
              </div>

              {/* Resumo */}
              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Resumo da memoria</span>
                  <Tooltip
                    title="Breve descricao usada pela IA para decidir se deve consultar esta memoria. Gerado automaticamente, mas voce pode editar."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                  <textarea
                    className={classes.configTextarea}
                    value={memSummary}
                    onChange={function (e) { setMemSummary(e.target.value); }}
                    placeholder="Clique em gerar para criar um resumo automatico..."
                    style={{ minHeight: 50, flex: 1 }}
                  />
                  <Tooltip title="Gerar resumo com IA" placement="top">
                    <button
                      className={classes.configAddBtn}
                      onClick={generateSummaryFn}
                      disabled={generating || !memContent.trim()}
                      type="button"
                      style={{ height: 36, width: 36, flexShrink: 0 }}
                    >
                      {generating ? (
                        <CircularProgress size={14} style={{ color: "#ec4899" }} />
                      ) : (
                        <Sparkles size={14} color="#ec4899" />
                      )}
                    </button>
                  </Tooltip>
                </div>
              </div>

              {/* Botao adicionar/salvar */}
              <div className={classes.configField}>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    className={classes.saveBtn}
                    onClick={addMemory}
                    disabled={!memName.trim() || !memContent.trim()}
                    style={{ flex: 1, justifyContent: "center" }}
                  >
                    {editingId ? (
                      <React.Fragment>
                        <Check size={14} />
                        Salvar alteracoes
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        <Plus size={14} />
                        Adicionar memoria
                      </React.Fragment>
                    )}
                  </button>
                  {editingId && (
                    <button
                      className={classes.configAddBtn}
                      onClick={cancelEdit}
                      type="button"
                      style={{ height: 36, width: 36 }}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* Lista de memorias */}
              {memories.length > 0 && (
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Memorias adicionadas ({memories.length})</span>
                  </div>
                  <div className={classes.configFileList}>
                    {memories.map(function (mem) {
                      var isExpanded = expandedId === mem.id;
                      return (
                        <div key={mem.id} style={{ marginBottom: 4 }}>
                          <div className={classes.configFileItem} style={{ cursor: "pointer" }} onClick={function () { setExpandedId(isExpanded ? null : mem.id); }}>
                            <div className={classes.configFileIcon} style={{ background: "#ec489918" }}>
                              <Brain size={13} color="#ec4899" strokeWidth={2} />
                            </div>
                            <div className={classes.configFileInfo}>
                              <div className={classes.configFileName}>{mem.name}</div>
                              <div className={classes.configFileSize} style={{ fontSize: 10, opacity: 0.5 }}>
                                {mem.summary || mem.content.substring(0, 60) + "..."}
                              </div>
                            </div>
                            <span style={{ opacity: 0.4, marginRight: 4, display: "flex" }}>
                              {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            </span>
                            <span
                              className={classes.configFileRemove}
                              onClick={function (e) { e.stopPropagation(); editMemory(mem); }}
                              style={{ marginRight: 2 }}
                            >
                              <Pencil size={11} />
                            </span>
                            <span
                              className={classes.configFileRemove}
                              onClick={function (e) { e.stopPropagation(); removeMemory(mem.id); }}
                            >
                              <Trash2 size={12} />
                            </span>
                          </div>
                          {isExpanded && (
                            <div style={{
                              padding: "8px 12px",
                              fontSize: 11,
                              lineHeight: 1.5,
                              color: "rgba(255,255,255,0.7)",
                              background: "rgba(236,72,153,0.05)",
                              borderRadius: "0 0 8px 8px",
                              borderTop: "1px solid rgba(236,72,153,0.1)",
                              whiteSpace: "pre-wrap",
                              maxHeight: 200,
                              overflowY: "auto",
                            }}>
                              {mem.content}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {memories.length === 0 && (
                <div className={classes.configField}>
                  <div className={classes.configEmpty}>
                    Nenhuma memoria adicionada
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })()}

        {cfg.type === "tool_websearch" && (function () {
          var domains = nodeData.domains || [];
          var domainInputRef = React.createRef();

          var formatDomain = function (raw) {
            var d = raw.trim().toLowerCase();
            d = d.replace(/^https?:\/\//, "");
            d = d.replace(/^www\./, "");
            d = d.replace(/\/.*$/, "");
            d = d.replace(/\s/g, "");
            return d;
          };

          var addDomain = function () {
            var input = domainInputRef.current;
            if (!input) return;
            var raw = input.value;
            if (!raw || !raw.trim()) return;
            var formatted = formatDomain(raw);
            if (!formatted || formatted.indexOf(".") === -1) return;
            if (domains.indexOf(formatted) !== -1) {
              input.value = "";
              return;
            }
            updateNodeData("domains", domains.concat(formatted));
            input.value = "";
          };

          var removeDomain = function (domain) {
            updateNodeData("domains", domains.filter(function (d) { return d !== domain; }));
          };

          var handleKeyDown = function (e) {
            if (e.key === "Enter") {
              e.preventDefault();
              addDomain();
            }
          };

          return (
            <React.Fragment>
              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Dominios permitidos</span>
                  <Tooltip
                    title="A IA so vai pesquisar nesses dominios. Adicione os sites confiaveis que a IA pode consultar. URLs sao formatadas automaticamente."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>

                <div className={classes.configInputRow}>
                  <input
                    ref={domainInputRef}
                    className={classes.configInput}
                    placeholder="ex: meusite.com.br"
                    onKeyDown={handleKeyDown}
                  />
                  <button
                    className={classes.configAddBtn}
                    onClick={addDomain}
                    type="button"
                  >
                    <Plus size={14} />
                  </button>
                </div>

                {domains.length > 0 && (
                  <div className={classes.configDomainList}>
                    {domains.map(function (domain) {
                      return (
                        <div key={domain} className={classes.configDomainItem}>
                          <Globe size={12} className={classes.configDomainIcon} />
                          <span className={classes.configDomainText}>{domain}</span>
                          <span
                            className={classes.configDomainRemove}
                            onClick={function () { removeDomain(domain); }}
                          >
                            <Trash2 size={12} />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {domains.length === 0 && (
                  <div className={classes.configEmpty}>
                    Nenhum dominio adicionado
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })()}

        {cfg.type === "tool_woocommerce" && (function () {
          var selectedIds = nodeData.integrationIds || [];
          var toggleIntegration = function (id) {
            var idx = selectedIds.indexOf(id);
            if (idx === -1) {
              updateNodeData("integrationIds", selectedIds.concat(id));
            } else {
              updateNodeData("integrationIds", selectedIds.filter(function (x) { return x !== id; }));
            }
          };

          var anyStale = selectedIds.some(function (id) {
            var integ = wooIntegrations.find(function (w) { return w.id === id; });
            if (!integ) return false;
            return !integ.lastSyncAt || integ.lastSyncStatus === "error";
          });

          return (
            <React.Fragment>
              <div className={classes.configField}>
                <div style={{
                  padding: "10px 12px",
                  background: "rgba(147, 51, 234, 0.08)",
                  borderRadius: 8,
                  fontSize: 11,
                  lineHeight: 1.5,
                  color: "rgba(255,255,255,0.7)",
                }}>
                  A IA podera buscar produtos no catalogo das lojas selecionadas durante a conversa.
                  O catalogo e sincronizado periodicamente no plano de fundo.
                </div>
              </div>

              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Lojas WooCommerce</span>
                  <Tooltip
                    title="Selecione uma ou mais lojas. A IA buscara produtos em todas elas e responder com o resultado mais relevante."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>

                {wooIntegrations.length === 0 && (
                  <div className={classes.configEmpty} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <AlertTriangle size={14} color="#f59e0b" />
                    <span>Nenhuma loja cadastrada. Cadastre em <strong>Configuracoes &gt; Integracoes</strong>.</span>
                  </div>
                )}

                {wooIntegrations.length > 0 && (
                  <div className={classes.configDropdown} style={{ maxHeight: 240 }}>
                    {wooIntegrations.map(function (integ) {
                      var isSelected = selectedIds.indexOf(integ.id) !== -1;
                      var inactive = integ.isActive === false;
                      var hasError = integ.lastSyncStatus === "error";
                      var neverSynced = !integ.lastSyncAt;
                      return (
                        <div
                          key={integ.id}
                          className={classes.configDropdownItem}
                          onClick={function () { if (!inactive) toggleIntegration(integ.id); }}
                          style={inactive ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                        >
                          <div
                            className={classes.configDropdownCheck + (isSelected ? " " + classes.configDropdownCheckActive : "")}
                          >
                            {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                          </div>
                          <ShoppingBag size={12} style={{ color: "#9333ea", flexShrink: 0 }} />
                          <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 12, fontWeight: 500 }}>{integ.name}</span>
                            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", display: "flex", gap: 6, alignItems: "center" }}>
                              {integ.productCount || 0} produtos
                              <span style={{ color: "rgba(255,255,255,0.25)" }}>•</span>
                              <span style={{ color: hasError ? "#ef4444" : neverSynced ? "#f59e0b" : "rgba(255,255,255,0.4)" }}>
                                {hasError ? "erro no ultimo sync" : "sync " + formatRelativeTime(integ.lastSyncAt, "nunca sincronizado")}
                              </span>
                              {inactive && (
                                <React.Fragment>
                                  <span style={{ color: "rgba(255,255,255,0.25)" }}>•</span>
                                  <span style={{ color: "#ef4444" }}>inativa</span>
                                </React.Fragment>
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {anyStale && (
                  <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: "#f59e0b" }}>
                    <AlertTriangle size={12} />
                    <span>Alguma loja selecionada nao sincronizou ainda — verifique em Integracoes.</span>
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })()}

        {/* ═══ CRIAR CARD CRM ═══ */}
        {cfg.type === "tool_crm_create" && (function () {
          var fields = nodeData.fields || [];
          var selectedBoardId = nodeData.boardId || null;

          var handleBoardChange = function (boardId) {
            updateNodeData("boardId", boardId);
          };

          var addField = function () {
            var name = crmFieldName.trim();
            if (!name) {
              toastError({ response: { data: { error: "Digite o nome do campo" } } });
              return;
            }
            if (fields.some(function (f) { return f.name.toLowerCase() === name.toLowerCase(); })) {
              toastError({ response: { data: { error: "Campo ja existe" } } });
              return;
            }
            var newField = {
              id: Date.now() + "-" + Math.random().toString(36).substr(2, 6),
              name: name,
            };
            updateNodeData("fields", fields.concat(newField));
            setCrmFieldName("");
          };

          var removeField = function (fieldId) {
            updateNodeData("fields", fields.filter(function (f) { return f.id !== fieldId; }));
          };

          return (
            <React.Fragment>
              <div className={classes.configField}>
                <div style={{
                  padding: "10px 12px",
                  background: "rgba(16, 185, 129, 0.08)",
                  borderRadius: 8,
                  fontSize: 11,
                  lineHeight: 1.5,
                  color: "rgba(255,255,255,0.7)",
                }}>
                  A IA vai coletar todos os campos abaixo durante a conversa. O card so sera criado no CRM quando <strong style={{ color: "#10b981" }}>todos os campos</strong> forem preenchidos.
                </div>
              </div>

              {/* Seletor de quadro */}
              {crmBoards.length > 1 && (
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Quadro CRM</span>
                    <Tooltip
                      title="Em qual quadro do CRM o card sera criado. Se nao selecionado, usa o quadro padrao."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  <div className={classes.configDropdown} style={{ maxHeight: 150 }}>
                    {crmBoards.map(function (board) {
                      var isSelected = selectedBoardId === board.id;
                      return (
                        <div
                          key={board.id}
                          className={classes.configDropdownItem}
                          onClick={function () { handleBoardChange(board.id); }}
                        >
                          <div
                            className={classes.configDropdownCheck + (isSelected ? " " + classes.configDropdownCheckActive : "")}
                          >
                            {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                          </div>
                          <span className={classes.configDropdownColor} style={{ background: board.color || "#5B7CFF" }} />
                          {board.name}
                          {board.isDefault && <span style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", marginLeft: 4 }}>(padrao)</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Adicionar campo</span>
                  <Tooltip
                    title="Nome do campo que a IA precisa coletar. Ex: Nome completo, Telefone, Endereco, CPF, etc."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>
                <div className={classes.configInputRow}>
                  <input
                    className={classes.configInput}
                    value={crmFieldName}
                    onChange={function (e) { setCrmFieldName(e.target.value); }}
                    placeholder="ex: Nome completo"
                    onKeyDown={function (e) { if (e.key === "Enter") { e.preventDefault(); addField(); } }}
                  />
                  <button
                    className={classes.configAddBtn}
                    onClick={addField}
                    type="button"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>

              {fields.length > 0 && (
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Campos obrigatorios ({fields.length})</span>
                  </div>
                  <div className={classes.configDomainList}>
                    {fields.map(function (field) {
                      return (
                        <div key={field.id} className={classes.configDomainItem}>
                          <SquareKanban size={12} style={{ color: "#10b981", flexShrink: 0 }} />
                          <span className={classes.configDomainText}>{field.name}</span>
                          <span
                            className={classes.configDomainRemove}
                            onClick={function () { removeField(field.id); }}
                          >
                            <Trash2 size={12} />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {fields.length === 0 && (
                <div className={classes.configField}>
                  <div className={classes.configEmpty}>
                    Nenhum campo adicionado
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })()}

        {/* ═══ ATUALIZAR CARD CRM ═══ */}
        {cfg.type === "tool_crm_update" && (function () {
          var selectedBoardId = nodeData.boardId || null;
          return (
            <React.Fragment>
              <div className={classes.configField}>
                <div style={{
                  padding: "10px 12px",
                  background: "rgba(6, 182, 212, 0.08)",
                  borderRadius: 8,
                  fontSize: 11,
                  lineHeight: 1.5,
                  color: "rgba(255,255,255,0.7)",
                }}>
                  <strong style={{ color: "#06b6d4" }}>Atualizar Card</strong>
                  <br /><br />
                  Quando conectado ao agente, a IA detecta automaticamente quando o cliente corrige ou atualiza uma informacao que ja foi coletada.
                  <br /><br />
                  <strong>Exemplo:</strong> se a IA coletou o endereco "Rua A, 100" e depois o cliente diz "na verdade e Rua B, 200", a IA vai atualizar o campo no card do CRM automaticamente.
                  <br /><br />
                  Nao e necessario configurar nada — basta conectar este node ao agente.
                </div>
              </div>

              {/* Seletor de quadro */}
              {crmBoards.length > 1 && (
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Quadro CRM</span>
                  </div>
                  <div className={classes.configDropdown} style={{ maxHeight: 150 }}>
                    {crmBoards.map(function (board) {
                      var isSelected = selectedBoardId === board.id;
                      return (
                        <div
                          key={board.id}
                          className={classes.configDropdownItem}
                          onClick={function () { updateNodeData("boardId", board.id); }}
                        >
                          <div
                            className={classes.configDropdownCheck + (isSelected ? " " + classes.configDropdownCheckActive : "")}
                          >
                            {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                          </div>
                          <span className={classes.configDropdownColor} style={{ background: board.color || "#5B7CFF" }} />
                          {board.name}
                          {board.isDefault && <span style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", marginLeft: 4 }}>(padrao)</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })()}

        {/* ═══ STATUS CARD CRM ═══ */}
        {cfg.type === "tool_crm_status" && (function () {
          var selectedBoardId = nodeData.boardId || null;
          var startColumn = nodeData.startColumn || null;
          var endColumn = nodeData.endColumn || null;
          var middleColumns = nodeData.middleColumns || [];
          var sortedStatuses = leadStatuses.slice().sort(function (a, b) { return (a.order || 0) - (b.order || 0); });

          var handleBoardChangeStatus = function (boardId) {
            updateNodeData("boardId", boardId);
            // Resetar colunas ao trocar de board
            updateNodeData("startColumn", null);
            updateNodeData("endColumn", null);
            updateNodeData("middleColumns", []);
            // Recarregar statuses do board selecionado
            var url = boardId ? "/lead-statuses?boardId=" + boardId : "/lead-statuses";
            api.get(url).then(function (res) { setLeadStatuses(res.data); }).catch(function () {});
          };

          var setStartColumn = function (id) {
            updateNodeData("startColumn", id);
          };
          var setEndColumn = function (id) {
            updateNodeData("endColumn", id);
          };
          var addMiddleColumn = function (id) {
            if (middleColumns.indexOf(id) !== -1) return;
            updateNodeData("middleColumns", middleColumns.concat(id));
          };
          var removeMiddleColumn = function (id) {
            updateNodeData("middleColumns", middleColumns.filter(function (c) { return c !== id; }));
          };

          var getColumnName = function (id) {
            var found = leadStatuses.find(function (s) { return s.id === id; });
            return found ? found.name : "?";
          };
          var getColumnColor = function (id) {
            var found = leadStatuses.find(function (s) { return s.id === id; });
            return found ? found.color : "#666";
          };

          // Available columns (excluding already selected)
          var usedIds = [startColumn, endColumn].concat(middleColumns).filter(Boolean);
          var availableColumns = sortedStatuses.filter(function (s) { return usedIds.indexOf(s.id) === -1; });

          return (
            <React.Fragment>
              <div className={classes.configField}>
                <div style={{
                  padding: "10px 12px",
                  background: "rgba(249, 115, 22, 0.08)",
                  borderRadius: 8,
                  fontSize: 11,
                  lineHeight: 1.5,
                  color: "rgba(255,255,255,0.7)",
                }}>
                  Defina a jornada de status que a IA vai seguir. Ela começa na <strong style={{ color: "#f97316" }}>coluna inicial</strong> e vai movendo o card ate a <strong style={{ color: "#f97316" }}>coluna de conclusao</strong>.
                </div>
              </div>

              {/* Seletor de quadro */}
              {crmBoards.length > 1 && (
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Quadro CRM</span>
                    <Tooltip
                      title="Selecione o quadro cujas colunas serao usadas para a jornada de status."
                      placement="top"
                      classes={{ tooltip: classes.configInfoTooltip }}
                    >
                      <span className={classes.configInfoIcon}>
                        <Info size={13} />
                      </span>
                    </Tooltip>
                  </div>
                  <div className={classes.configDropdown} style={{ maxHeight: 150 }}>
                    {crmBoards.map(function (board) {
                      var isSelected = selectedBoardId === board.id;
                      return (
                        <div
                          key={board.id}
                          className={classes.configDropdownItem}
                          onClick={function () { handleBoardChangeStatus(board.id); }}
                        >
                          <div
                            className={classes.configDropdownCheck + (isSelected ? " " + classes.configDropdownCheckActive : "")}
                          >
                            {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                          </div>
                          <span className={classes.configDropdownColor} style={{ background: board.color || "#5B7CFF" }} />
                          {board.name}
                          {board.isDefault && <span style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", marginLeft: 4 }}>(padrao)</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Coluna inicial */}
              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Coluna inicial</span>
                </div>
                <div className={classes.configDropdown} style={{ maxHeight: 150 }}>
                  {sortedStatuses.map(function (s) {
                    var isSelected = startColumn === s.id;
                    return (
                      <div
                        key={s.id}
                        className={classes.configDropdownItem}
                        onClick={function () { setStartColumn(s.id); }}
                      >
                        <div
                          className={classes.configDropdownCheck + (isSelected ? " " + classes.configDropdownCheckActive : "")}
                        >
                          {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                        </div>
                        <span className={classes.configDropdownColor} style={{ background: s.color || "#666" }} />
                        {s.name}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Colunas intermediarias */}
              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Colunas intermediarias</span>
                  <Tooltip
                    title="Opcional. Adicione colunas pelas quais o card deve passar antes da conclusao. A IA move o card conforme o progresso."
                    placement="top"
                    classes={{ tooltip: classes.configInfoTooltip }}
                  >
                    <span className={classes.configInfoIcon}>
                      <Info size={13} />
                    </span>
                  </Tooltip>
                </div>

                {middleColumns.length > 0 && (
                  <div className={classes.configDomainList} style={{ marginBottom: 6 }}>
                    {middleColumns.map(function (colId, idx) {
                      return (
                        <div key={colId} className={classes.configDomainItem}>
                          <span className={classes.configDropdownColor} style={{ background: getColumnColor(colId), flexShrink: 0 }} />
                          <span className={classes.configDomainText}>{getColumnName(colId)}</span>
                          <span
                            className={classes.configDomainRemove}
                            onClick={function () { removeMiddleColumn(colId); }}
                          >
                            <Trash2 size={12} />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {availableColumns.length > 0 && (
                  <div className={classes.configDropdown} style={{ maxHeight: 120 }}>
                    {availableColumns.map(function (s) {
                      return (
                        <div
                          key={s.id}
                          className={classes.configDropdownItem}
                          onClick={function () { addMiddleColumn(s.id); }}
                        >
                          <Plus size={10} style={{ marginRight: 4, opacity: 0.5 }} />
                          <span className={classes.configDropdownColor} style={{ background: s.color || "#666" }} />
                          {s.name}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Coluna de conclusao */}
              <div className={classes.configField}>
                <div className={classes.configLabelRow}>
                  <span className={classes.configLabel}>Coluna de conclusao</span>
                </div>
                <div className={classes.configDropdown} style={{ maxHeight: 150 }}>
                  {sortedStatuses.map(function (s) {
                    var isSelected = endColumn === s.id;
                    return (
                      <div
                        key={s.id}
                        className={classes.configDropdownItem}
                        onClick={function () { setEndColumn(s.id); }}
                      >
                        <div
                          className={classes.configDropdownCheck + (isSelected ? " " + classes.configDropdownCheckActive : "")}
                        >
                          {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                        </div>
                        <span className={classes.configDropdownColor} style={{ background: s.color || "#666" }} />
                        {s.name}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Visual da jornada */}
              {startColumn && endColumn && (
                <div className={classes.configField}>
                  <div className={classes.configLabelRow}>
                    <span className={classes.configLabel}>Jornada do card</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: getColumnColor(startColumn), flexShrink: 0 }} />
                      <span style={{ color: "rgba(255,255,255,0.8)" }}>{getColumnName(startColumn)}</span>
                      <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 9 }}>(inicio)</span>
                    </div>
                    {middleColumns.map(function (colId) {
                      return (
                        <div key={colId} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, paddingLeft: 12 }}>
                          <span style={{ color: "rgba(255,255,255,0.3)" }}>↓</span>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: getColumnColor(colId), flexShrink: 0 }} />
                          <span style={{ color: "rgba(255,255,255,0.7)" }}>{getColumnName(colId)}</span>
                        </div>
                      );
                    })}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, paddingLeft: middleColumns.length > 0 ? 12 : 0 }}>
                      {middleColumns.length > 0 && <span style={{ color: "rgba(255,255,255,0.3)" }}>↓</span>}
                      {middleColumns.length === 0 && <span style={{ color: "rgba(255,255,255,0.3)", marginRight: 2 }}>→</span>}
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: getColumnColor(endColumn), flexShrink: 0 }} />
                      <span style={{ color: "rgba(255,255,255,0.8)" }}>{getColumnName(endColumn)}</span>
                      <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 9 }}>(conclusao)</span>
                    </div>
                  </div>
                </div>
              )}

              {leadStatuses.length === 0 && (
                <div className={classes.configField}>
                  <div className={classes.configEmpty}>
                    Nenhuma coluna CRM encontrada. Crie colunas em CRM &gt; Configuracoes.
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })()}
      </div>

      <AiToolsGuideModal
        open={guideOpen}
        onClose={function () { setGuideOpen(false); }}
      />
    </div>
  );
};

export default ConfigPanel;
